function fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: {'Content-Type': 'application/json', 'Accept': `application/${type}`}
	};
}
function debounce(fn, delay) {
	let timeoutId;
	return function (...args) {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => fn.apply(this, args), delay);
	};
}

let globalSliderObserver = null;
let productCardVideoObserver = null;
const productCardSwiperRegistry = new WeakMap();
const productCardMobileMedia = window.matchMedia('(max-width: 768px)');

function isIOSDevice() {
	return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
		(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function getProductCardVideos(root = document) {
	if (!root?.querySelectorAll) return [];
	return Array.from(root.querySelectorAll('.product-card__inner-slider video:not(.is-video-observed)'));
}

function getProductCardSliders(root = document) {
	if (!root) return [];

	const sliders = [];

	if (root.matches?.('.product-card__inner-slider')) {
		sliders.push(root);
	}

	if (root.querySelectorAll) {
		sliders.push(...root.querySelectorAll('.product-card__inner-slider'));
	}

	return sliders;
}

function loadProductCardDeferredImage(image) {
	if (!(image instanceof HTMLImageElement) || !image.hasAttribute('data-product-card-deferred')) return;

	const picture = image.closest('picture');
	picture?.querySelectorAll('source[data-product-card-deferred-source]').forEach((source) => {
		if (source.dataset.srcset) source.srcset = source.dataset.srcset;
		source.removeAttribute('data-srcset');
		source.removeAttribute('data-product-card-deferred-source');
	});

	if (image.dataset.srcset) image.srcset = image.dataset.srcset;
	if (image.dataset.sizes) image.sizes = image.dataset.sizes;
	if (image.dataset.src) image.src = image.dataset.src;

	image.removeAttribute('data-src');
	image.removeAttribute('data-srcset');
	image.removeAttribute('data-sizes');
	image.removeAttribute('data-product-card-deferred');
	image.classList.remove('product-card__deferred-image');
}

function loadProductCardSlide(slider, index) {
	const slide = slider?.querySelectorAll('.swiper-slide')?.[index];
	slide?.querySelectorAll('img[data-product-card-deferred]').forEach(loadProductCardDeferredImage);
}

function loadAllProductCardDeferredImages(slider) {
	slider?.querySelectorAll('img[data-product-card-deferred]').forEach(loadProductCardDeferredImage);
}

function ensureProductCardDeferredMedia(slider) {
	if (!(slider instanceof HTMLElement) || slider.dataset.productCardDeferredBound === 'true') return;

	slider.dataset.productCardDeferredBound = 'true';
	const loadAll = () => loadAllProductCardDeferredImages(slider);
	slider.addEventListener('mouseenter', loadAll, { once: true });
	slider.addEventListener('focusin', loadAll, { once: true });
	slider.addEventListener('pointerdown', loadAll, { once: true, passive: true });
	slider.addEventListener('touchstart', loadAll, { once: true, passive: true });
}

function scheduleProductCardSecondImage(slider) {
	if (!(slider instanceof HTMLElement) || slider.dataset.productCardSecondImageScheduled === 'true') return;

	slider.dataset.productCardSecondImageScheduled = 'true';
	window.setTimeout(() => {
		slider.removeAttribute('data-product-card-second-image-scheduled');
		if (!slider.isConnected) return;

		const rect = slider.getBoundingClientRect();
		const isNearViewport = rect.bottom >= -300 && rect.top <= window.innerHeight + 300;
		if (isNearViewport) loadProductCardSlide(slider, 1);
	}, 250);
}

function shouldUseProductCardSwiper(slider) {
	if (!(slider instanceof HTMLElement)) return false;

	return slider.querySelectorAll('.swiper-slide').length > 1;
}

function getProductCardSwiperMode() {
	return productCardMobileMedia.matches ? 'mobile' : 'desktop';
}

function destroyProductCardSwiper(slider) {
	if (!(slider instanceof HTMLElement)) return;

	const swiperInstance = productCardSwiperRegistry.get(slider) || slider.swiper;

	if (swiperInstance && !swiperInstance.destroyed) {
		try {
			swiperInstance.destroy(true, true);
		} catch (error) {
			console.warn('Failed to destroy product card swiper:', error);
		}
	}

	productCardSwiperRegistry.delete(slider);
	slider.removeAttribute('data-product-card-swiper-mode');
}

function ensureProductCardDesktopHover(slider) {
	if (slider.dataset.productCardHoverBound === 'true') return;

	slider.dataset.productCardHoverBound = 'true';
	slider.addEventListener('mouseenter', () => {
		if (productCardMobileMedia.matches || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
		const swiperInstance = productCardSwiperRegistry.get(slider) || slider.swiper;
		if (swiperInstance && !swiperInstance.destroyed) swiperInstance.slideTo(1, 260);
	});
	slider.addEventListener('mouseleave', () => {
		if (productCardMobileMedia.matches) return;
		const swiperInstance = productCardSwiperRegistry.get(slider) || slider.swiper;
		if (swiperInstance && !swiperInstance.destroyed) swiperInstance.slideTo(0, 260);
	});
}

function createProductCardSwiper(slider) {
	if (!(slider instanceof HTMLElement)) return;

	ensureProductCardDeferredMedia(slider);
	scheduleProductCardSecondImage(slider);

	if (!shouldUseProductCardSwiper(slider)) {
		destroyProductCardSwiper(slider);
		return;
	}

	const mode = getProductCardSwiperMode();
	const existingSwiper = productCardSwiperRegistry.get(slider) || slider.swiper;
	if (existingSwiper && !existingSwiper.destroyed) {
		if (slider.dataset.productCardSwiperMode === mode) {
			productCardSwiperRegistry.set(slider, existingSwiper);
			return;
		}
		destroyProductCardSwiper(slider);
	}

	const swiperOptions = {
		slidesPerView: 1,
		speed: 260,
		watchOverflow: true,
		lazy: {
			loadPrevNext: true,
		},
	};

	if (mode === 'mobile') {
		swiperOptions.pagination = {
			el: slider.querySelector('.product-card__pagination'),
			clickable: false,
		};
	} else {
		swiperOptions.navigation = {
			nextEl: slider.querySelector('.swiper-button-next'),
			prevEl: slider.querySelector('.swiper-button-prev'),
		};
	}

	const swiperInstance = new Swiper(slider, swiperOptions);

	productCardSwiperRegistry.set(slider, swiperInstance);
	slider.dataset.productCardSwiperMode = mode;
	if (mode === 'desktop') ensureProductCardDesktopHover(slider);
}

function refreshProductCardSlidersForViewport() {
	getProductCardSliders(document).forEach((slider) => {
		const rect = slider.getBoundingClientRect();
		const isNearViewport = rect.bottom >= -300 && rect.top <= window.innerHeight + 300;
		if (isNearViewport) createProductCardSwiper(slider);
	});
}

if (productCardMobileMedia.addEventListener) {
	productCardMobileMedia.addEventListener('change', refreshProductCardSlidersForViewport);
} else {
	productCardMobileMedia.addListener(refreshProductCardSlidersForViewport);
}

function configureProductCardVideo(video) {
	if (!(video instanceof HTMLVideoElement)) return;

	video.playsInline = true;
	video.muted = true;
	video.loop = true;
	video.preload = 'none';

	// iOS Safari is especially sensitive to many autoplaying videos in long lists.
	if (isIOSDevice()) {
		video.autoplay = false;
		video.removeAttribute('autoplay');
		video.controls = false;
		video.removeAttribute('controls');
	}
}

function playProductCardVideo(video) {
	if (!(video instanceof HTMLVideoElement)) return;

	const playPromise = video.play();
	if (playPromise?.catch) {
		playPromise.catch(() => {});
	}
}

function pauseProductCardVideo(video) {
	if (!(video instanceof HTMLVideoElement)) return;

	video.pause();
}

function ensureProductCardVideoObserver() {
	if (productCardVideoObserver) return;

	productCardVideoObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				const video = entry.target;

				if (!(video instanceof HTMLVideoElement)) return;

				if (entry.isIntersecting) {
					playProductCardVideo(video);
				} else {
					pauseProductCardVideo(video);
				}
			});
		},
		{
			threshold: 0.25,
			rootMargin: '200px 0px'
		}
	);
}

window.syncProductCardVideos = function (root = document) {
	const videos = getProductCardVideos(root);
	if (!videos.length) return;

	ensureProductCardVideoObserver();

	videos.forEach((video) => {
		video.classList.add('is-video-observed');
		configureProductCardVideo(video);
		productCardVideoObserver.observe(video);
	});
};

window.destroyProductCardMedia = function (root = document) {
	if (!root?.querySelectorAll) return;

	root.querySelectorAll('.product-card__inner-slider').forEach((slider) => {
		globalSliderObserver?.unobserve(slider);
		slider.classList.remove('is-observed');
		destroyProductCardSwiper(slider);
	});

	getProductCardVideos(root).forEach((video) => {
		productCardVideoObserver?.unobserve(video);
		pauseProductCardVideo(video);
	});
};

window.syncProductCardMedia = function (root = document) {
	initSlider(root);
	window.syncProductCardVideos?.(root);
};

function initSlider(root = document) {
	const newSliders = getProductCardSliders(root).filter((slider) => !slider.classList.contains('is-observed'));

	if (newSliders.length === 0) return;

	if (!globalSliderObserver) {
		globalSliderObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const target = entry.target;

					if (entry.isIntersecting) {
						createProductCardSwiper(target);
						return;
					}

					destroyProductCardSwiper(target);
				});
			},
			{
				threshold: 0.01,
				rootMargin: '300px 0px'
			}
		);
	}

	newSliders.forEach((slider) => {
		slider.classList.add('is-observed');
		globalSliderObserver.observe(slider);
	});
}

window.addEventListener('globoFilterRenderCompleted', function (e) {
    initSlider();
});

document.addEventListener('DOMContentLoaded', () => {
	window.syncProductCardMedia?.();
});

// Related products

function runWhenProductBlockNearViewport(element, callback, distance = 800) {
	const anchor = element.closest('.shopify-section') || element;
	let frameId = null;
	let finished = false;

	const cleanup = () => {
		window.removeEventListener('scroll', scheduleCheck);
		window.removeEventListener('resize', scheduleCheck);
	};

	const check = () => {
		frameId = null;
		if (finished || !anchor.isConnected) return;

		const rect = anchor.getBoundingClientRect();
		if (rect.top > window.innerHeight + distance || rect.bottom < -distance) return;

		finished = true;
		cleanup();
		callback();
	};

	const scheduleCheck = () => {
		if (frameId !== null || finished) return;
		frameId = window.requestAnimationFrame(check);
	};

	window.addEventListener('scroll', scheduleCheck, { passive: true });
	window.addEventListener('resize', scheduleCheck);
	scheduleCheck();
}

if (!customElements.get('product-recommendation')) {
	customElements.define('product-recommendation', class ProductRecommendation extends HTMLElement {
		constructor() {
			super();
			runWhenProductBlockNearViewport(this, () => this.renderSection());
		}

		async renderSection() {
			try {
				const response = await fetch(this.dataset.url);
				const responseText = await response.text();
				const doc = new DOMParser().parseFromString(responseText, 'text/html');

				const fetchSection = doc.querySelector('[data-product-list]');
				const section = this.querySelector('[data-product-list]');

				if (fetchSection?.innerHTML.trim() && section) {
					section.innerHTML = fetchSection.innerHTML;
					this.removeAttribute('style');
					initProductsSlider();
					initSlider();

				}

				if (window.lazyLoadEnabled === "true") {
					reloadLazyLoad();
				}

			} catch (err) {
				console.error('Failed to load product recommendations:', err);
			}
		}
	});
}

// Recently viewed products

document.addEventListener('DOMContentLoaded', () => {
	const saveProduct = () => {
		const KEY = 'recently_viewed_handles';
		const MAX = 10;

		if (!window.viewed_product) return;

		let items = JSON.parse(localStorage.getItem(KEY)) || [];

		items = items.filter(h => h !== window.viewed_product.handle);
		items.unshift(window.viewed_product.handle);
		items = items.slice(0, MAX);

		localStorage.setItem(KEY, JSON.stringify(items));
	}

	saveProduct();
})

if (!customElements.get('viewed-products')) {
	customElements.define(
		'viewed-products',
		class RecentlyViewedProducts extends HTMLElement {
			constructor() {
				super();

				const handles = this.getHandles();

				if (!handles.length) {
					this.hideSection();
					return;
				}

				this.dataset.state = 'loading';
				this.setAttribute('aria-busy', 'true');
				this.style.removeProperty('display');
				runWhenProductBlockNearViewport(this, () => this.renderProducts(handles));
			}

			getHandles() {
				try {
					const storedHandles = JSON.parse(
						localStorage.getItem('recently_viewed_handles')
					);

					if (!Array.isArray(storedHandles)) return [];

					return [...new Set(
						storedHandles.filter(handle => typeof handle === 'string' && handle.trim())
					)].slice(0, 8);
				} catch (error) {
					return [];
				}
			}

			hideSection() {
				this.dataset.state = 'empty';
				this.removeAttribute('aria-busy');
				this.style.display = 'none';
			}

			async fetchProductCard(handle) {
				try {
					const storefrontRoot = window.Shopify?.routes?.root || '/';
					const res = await fetch(
						`${storefrontRoot}products/${encodeURIComponent(handle)}?section_id=product-card`
					);

					if (!res.ok) return '';

					const text = await res.text();
					const doc = new DOMParser().parseFromString(text, 'text/html');
					return doc.querySelector('.product-cards__card')?.outerHTML || '';
				} catch (error) {
					return '';
				}
			}

			async renderProducts(handles = this.getHandles()) {
				try {
					if (!handles.length) {
						this.hideSection();
						return;
					}

					const container = this.querySelector('[data-product-viewed-list]');
					if (!container) {
						this.dataset.state = 'error';
						this.removeAttribute('aria-busy');
						return;
					}

					const cards = await Promise.all(
						handles.map(handle => this.fetchProductCard(handle))
					);
					const html = cards.filter(Boolean).join('');

					if (!html) {
						this.dataset.state = 'error';
						this.removeAttribute('aria-busy');
						return;
					}

					container.innerHTML = html;
					this.dataset.state = 'ready';
					this.removeAttribute('aria-busy');
					this.style.removeProperty('display');
					initProductsSlider();
					initSlider();
				} catch (err) {
					this.dataset.state = 'error';
					this.removeAttribute('aria-busy');
					console.error('Recently viewed failed:', err);
				}
			}
		}
	);
}

// Cart popup

async function updateCartPopup() {
	try {
		const cartSectionWrapper = document.querySelector('.cart-drawer').closest('.shopify-section');
		let sectionId = 'ajax-cart-popup';

		if (cartSectionWrapper) {
			sectionId = cartSectionWrapper.id.replace('shopify-section-', '');
		}

		const fetchUrl = new URL(window.location.href);
		fetchUrl.searchParams.set('section_id', sectionId);

		const response = await fetch(fetchUrl.toString());
		const html = await response.text();
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, 'text/html');

		const elements = [
			{
				fetch: '.cart-drawer',
				current: '.cart-drawer',
				updateMethod: 'outerHTML'
			}
		];

		elements.forEach(({fetch, current, updateMethod}) => {
			const fetchedElement = doc.querySelectorAll(fetch);
			const currentElement = document.querySelectorAll(current);

			fetchedElement.forEach((fetchEl, index) => {
				const currentEl = currentElement[index];
				if (fetchEl && currentEl) {
					currentEl[updateMethod] = fetchEl[updateMethod];
				}
			});
		});
		openCartPopup();

	} catch (error) {
		console.error('Cart popup content update error:', error);
	}
}

function updateCartPopupEvents(){
	var quantityInput = document.querySelectorAll('popup-cart-item .quantity');
	if (quantityInput.length) {
		quantityInput.forEach(function (quantity) {
			var plus = quantity.querySelector('.quantity-button--plus');
			var minus = quantity.querySelector('.quantity-button--minus');
			var input = quantity.querySelector('.quantity-input');
			if (plus) {
				plus.addEventListener('click', function () {
					var value = Number(input.value);
					value++;
					input.value = value;
					if (value > 1) {
						minus.classList.remove('_disabled')
					}
				})
			}
			if (minus) {
				minus.addEventListener('click', function () {
					var value = Number(input.value);
					if (value > 1) {
						value--;
						input.value = value;
						if (value == 1) {
							minus.classList.add('_disabled')
						}
					}
				})
			}
		})
	}
}

async function updateCartCount() {
	try {
		const response = await fetch('/cart.js');
		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const cart = await response.json();
		const cartCountElements = document.querySelectorAll('[data-cart-count]');

		if (!cartCountElements.length) return;

		cartCountElements.forEach(element => {
			element.style.display = cart.item_count > 0 ? '' : 'none';
		});
	} catch (error) {
		console.error('Failed to update cart count:', error);
	}
}

function updateCartState() {
	const hasItems = $(".cart-item").length > 0;

	$(".cart-body, .cart-footer").toggle(hasItems);
	$(".cart-empty").toggle(!hasItems);
}
function closePopup() {
	$(".popup-wrapper, .popup-content").removeClass("active");
	$("html").css({ position: "static" });
	window.scroll(0, popupTop);
}
function openCartPopup(){
	$(".cart-drawer, .cart-overlay").addClass("open");
	_functions.openPopup();
	updateCartState();
}

function uploadFiles() {
	const uploadInstances = new Map();

	function initUploaders() {
		$(".upload-area").each(function () {
			const area = $(this);
			const input = area.find(".upload-input");
			const preview = area.closest(".form-group").find(".upload-preview");

			uploadInstances.set(area[0], {
				files: [],
				area,
				input,
				preview,
			});

			area.find(".upload-trigger").on("click", () => input.trigger("click"));

			input.on("change", function () {
				handleFiles(area, Array.from(this.files));
			});

			area.on("dragover", function (e) {
				e.preventDefault();
				area.addClass("dragover");
			});

			area.on("dragleave", function () {
				area.removeClass("dragover");
			});

			area.on("drop", function (e) {
				e.preventDefault();
				area.removeClass("dragover");
				let dt = e.originalEvent.dataTransfer;
				if (dt && dt.files.length) handleFiles(area, Array.from(dt.files));
			});
		});
	}

	function updateNativeInput(inst) {
		const dt = new DataTransfer();

		inst.files.forEach(file => {
			if (file) {
				dt.items.add(file);
			}
		});

		if (inst.input[0]) {
			inst.input[0].files = dt.files;
		}
	}

	function handleFiles(area, newFiles) {
		const inst = uploadInstances.get(area[0]);
		const currentActiveFiles = inst.files.filter(file => file !== null).length;
		const maxLimit = 2;

		if (currentActiveFiles + newFiles.length > maxLimit) {
			showUploadError(area, `You can upload a maximum of ${maxLimit} images.`);
			inst.input.val('');
			return;
		}

		for (let file of newFiles) {
			if (!file.type.match("image.*")) {
				showUploadError(area, "File must be an image");
				continue;
			}

			// Валідація розміру ≤ 15MB
			if (file.size > 15 * 1024 * 1024) {
				showUploadError(area, "Maximum file size is 15 MB");
				continue;
			}

			let index = inst.files.push(file) - 1;
			addPreview(inst, file, index);
		}

		updateNativeInput(inst);
	}

	function addPreview(inst, file, index) {
		const reader = new FileReader();
		reader.onload = function (e) {
			const preview = `
                <div class="preview-item" data-index="${index}">
                    <img src="${e.target.result}">
                    <div class="preview-remove">×</div>
                </div>`;
			inst.preview.append(preview);
		};
		reader.readAsDataURL(file);
	}

	$(document).on("click", ".preview-remove", function () {
		const item = $(this).closest(".preview-item");
		const index = item.data("index");

		const previewBox = item.closest(".upload-preview");
		const formGroup = item.closest(".form-group, .popup-container");
		const area = formGroup.find(".upload-area")[0];

		if (!area) return;

		const inst = uploadInstances.get(area);
		if (inst && inst.files) {
			inst.files[index] = null;

			updateNativeInput(inst);
		}

		item.remove();
	});

	function showUploadError(area, message) {
		let err = area.closest(".form-group").find(".upload-error");
		if (!err.length) {
			err = $('<div class="upload-error"></div>');
			area.closest(".form-group").append(err);
		}
		err.text(message).slideDown();
		setTimeout(() => err.slideUp(), 4000);
	}

	initUploaders();
}

//  Currency switcher

document.addEventListener("DOMContentLoaded", function() {
	// Map country ISO codes to locale ISO codes (based on available theme locales: de, en, fr, it)
	const COUNTRY_TO_LOCALE = {
		'DE': 'de', 'AT': 'de', 'CH': 'de', 'LI': 'de',
		'FR': 'fr', 'BE': 'fr', 'LU': 'fr', 'MC': 'fr',
		'IT': 'it', 'SM': 'it', 'VA': 'it',
		'LT': 'lt',
		'EE': 'et',
		'LV': 'lv',
	};

	const disclosures = document.querySelectorAll('.disclosure');

	disclosures.forEach(disclosure => {
		const toggle = disclosure.querySelector('[data-disclosure-toggle]');
		const list = disclosure.querySelector('[data-disclosure-list]');
		const input = disclosure.querySelector('[data-disclosure-input]');
		const form = disclosure.closest('form');

		if (!toggle || !list || !input || !form) return;

		toggle.addEventListener('click', function(event) {
			event.stopPropagation();

			const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
			closeAllDisclosures();

			if (!isExpanded) {
				list.classList.add('disclosure-list--visible');
				toggle.setAttribute('aria-expanded', 'true');
			} else {
				list.classList.remove('disclosure-list--visible');
				toggle.setAttribute('aria-expanded', 'false');
			}
		});

		const options = disclosure.querySelectorAll('[data-disclosure-option]');

		options.forEach(option => {
			option.addEventListener('click', function(event) {
				event.preventDefault(); // Забороняємо перехід за посиланням #

				const value = this.getAttribute('data-value');

				input.value = value;

				// When a country (currency) is selected, sync the locale input in the same form
				if (disclosure.hasAttribute('data-disclosure-currency')) {
					let localeInput = form.querySelector('input[name="locale_code"]');
					if (!localeInput) {
						// Locale selector may be hidden or absent — create the input dynamically
						localeInput = document.createElement('input');
						localeInput.type = 'hidden';
						localeInput.name = 'locale_code';
						form.appendChild(localeInput);
					}
					localeInput.value = COUNTRY_TO_LOCALE[value] || 'en';
				}

				form.submit();
			});
		});
	});

	document.addEventListener('click', function(event) {
		const isClickInside = event.target.closest('.disclosure');
		if (!isClickInside) {
			closeAllDisclosures();
		}
	});

	function closeAllDisclosures() {
		const allLists = document.querySelectorAll('.disclosure-list--visible');
		const allToggles = document.querySelectorAll('[aria-expanded="true"]');

		allLists.forEach(list => list.classList.remove('disclosure-list--visible'));
		allToggles.forEach(toggle => toggle.setAttribute('aria-expanded', 'false'));
	}
});

// Gender switcher
// document.addEventListener("DOMContentLoaded", function() {
// 	const genderLinks = document.querySelectorAll('[data-gender-switch]');
//
// 	genderLinks.forEach(link => {
// 		link.addEventListener('click', function(e) {
// 			e.preventDefault();
//
// 			const newGender = this.getAttribute('data-gender-switch');
// 			let targetUrl = this.getAttribute('href');
//
// 			// CACHE BUSTER: додаємо унікальну мітку часу до URL, щоб "пробити" HTML-кеш Shopify
// 			const timestamp = new Date().getTime();
// 			const separator = targetUrl.includes('?') ? '&' : '?';
// 			targetUrl = targetUrl + separator + 'mode_update=' + timestamp;
//
// 			fetch('/cart/update.js', {
// 				method: 'POST',
// 				headers: { 'Content-Type': 'application/json' },
// 				body: JSON.stringify({
// 					attributes: { '_gender_mode': newGender }
// 				})
// 			})
// 				.then(response => {
// 					if (!response.ok) throw new Error('Помилка оновлення сесії');
// 					window.location.href = targetUrl;
// 				})
// 				.catch(error => {
// 					console.error('Помилка при перемиканні:', error);
// 					window.location.href = targetUrl; // Переходимо навіть якщо сталася помилка
// 				});
// 		});
// 	});
// });

//  Localization popup

document.addEventListener('DOMContentLoaded', function() {
	const popup = document.getElementById('LocalizationPopup');
	const modalDialog = document.getElementById('LocPopupModal');
	const closeBtn = document.getElementById('CloseLocPopup');
	const form = document.getElementById('LocalizationPopupForm');
	const globalWrapper = document.querySelector('.popup-wrapper');
	const btnStay = document.getElementById('LocBtnStay');
	const countryInput = document.getElementById('TargetCountryCode');
	const languageInput = document.getElementById('TargetLanguageCode');
	const switchCountryNameEl = document.getElementById('SwitchCountryName');
	const switchCountryFlagEl = document.getElementById('SwitchCountryFlag');
	const currentCountryFlagEl = document.getElementById('CurrentCountryFlag');
	const translatedTitleEl = document.getElementById('LocPopupTranslatedTitle');
	const translatedMarketEl = document.getElementById('LocPopupTranslatedMarket');

	const isPopupLogicEnabled = popup ? popup.dataset.popupEnabled === 'true' : false;

	if (!popup || !modalDialog || !form || !isPopupLogicEnabled) return;

	function parseJsonElement(id, fallback) {
		const element = document.getElementById(id);
		if (!element) return fallback;
		try {
			return JSON.parse(element.textContent);
		} catch (error) {
			console.error('Failed to parse localization popup data.', error);
			return fallback;
		}
	}

	const countriesData = parseJsonElement('LocalizationPopupCountriesData', []);
	const externalStoresMap = parseJsonElement('LocalizationPopupExternalStoresData', {});
	const countriesMap = countriesData.reduce((acc, country) => {
		acc[country.iso_code.toUpperCase()] = country;
		return acc;
	}, {});

	const currentCountryCode = (popup.dataset.currentCountry || '').toUpperCase();
	const decisionKey = 'reytelRegionDecisionV1';

	function countryFlagEmoji(countryCode) {
		const normalizedCode = (countryCode || '').toUpperCase();
		if (!/^[A-Z]{2}$/.test(normalizedCode)) return normalizedCode;
		return Array.from(normalizedCode, character => String.fromCodePoint(127397 + character.charCodeAt(0))).join('');
	}

	function translatedRegionName(countryCode, languageCode) {
		try {
			return new Intl.DisplayNames([languageCode], { type: 'region' }).of(countryCode);
		} catch (error) {
			return countryCode;
		}
	}

	if (currentCountryFlagEl) currentCountryFlagEl.textContent = countryFlagEmoji(currentCountryCode);
	const legacySeenKey = 'localizationPopupSeen';
	const dayMs = 24 * 60 * 60 * 1000;
	let activeSuggestion = '';
	let externalTargetUrl = '';

	function readDecision() {
		try {
			return JSON.parse(localStorage.getItem(decisionKey) || 'null');
		} catch (error) {
			return null;
		}
	}

	function isSuggestionSuppressed(suggestion) {
		const decision = readDecision();
		if (!decision || decision.suggestion !== suggestion || !decision.timestamp) return false;
		const ttlDays = decision.decision === 'switch' ? 180 : 30;
		return Date.now() - Number(decision.timestamp) < ttlDays * dayMs;
	}

	function rememberDecision(decision) {
		if (!activeSuggestion) return;
		localStorage.setItem(decisionKey, JSON.stringify({
			suggestion: activeSuggestion,
			decision: decision,
			timestamp: Date.now(),
			version: 1
		}));
	}

	function openPopup() {
		if (globalWrapper) globalWrapper.classList.add('active');
		popup.classList.add('is-visible');
		document.documentElement.style.overflow = 'hidden';
	}

	function closePopup(decision) {
		if (globalWrapper) globalWrapper.classList.remove('active');
		popup.classList.remove('is-visible');
		document.documentElement.style.overflow = '';
		rememberDecision(decision || 'close');
	}

	function populateSuggestion(suggestion) {
		if (switchCountryNameEl) switchCountryNameEl.textContent = suggestion.name;
		if (switchCountryFlagEl) switchCountryFlagEl.textContent = countryFlagEmoji(suggestion.iso_code);
	}

	function populateExternalTranslation(externalStore) {
		const languageCode = externalStore.language_code || '';
		if (translatedTitleEl && externalStore.translated_title) {
			translatedTitleEl.textContent = externalStore.translated_title;
			translatedTitleEl.lang = languageCode;
			translatedTitleEl.hidden = false;
		}
		if (translatedMarketEl && externalStore.translated_market_text) {
			const countryName = translatedRegionName(currentCountryCode, languageCode);
			translatedMarketEl.textContent = externalStore.translated_market_text + ' ' + countryName;
			translatedMarketEl.lang = languageCode;
			translatedMarketEl.hidden = false;
		}
	}

	if (closeBtn) closeBtn.addEventListener('click', () => closePopup('close'));
	if (btnStay) btnStay.addEventListener('click', () => closePopup('stay'));
	popup.addEventListener('click', (event) => {
		if (event.target === popup) closePopup('close');
	});

	fetch(window.Shopify.routes.root + 'browsing_context_suggestions.json')
		.then(response => response.json())
		.then(data => {
			const detectedValues = data && data.detected_values ? data.detected_values : {};
			let detectedCountryCode = '';
			let detectedLanguageCode = '';

			if (detectedValues.country) {
				detectedCountryCode = (detectedValues.country.iso_code || detectedValues.country.handle || '').toUpperCase();
			}
			if (detectedValues.language) {
				detectedLanguageCode = (detectedValues.language.iso_code || detectedValues.language.handle || '').toLowerCase();
			}

			const externalStore = externalStoresMap[detectedCountryCode];
			if (externalStore && externalStore.url) {
				activeSuggestion = 'store:' + externalStore.key;
				if (isSuggestionSuppressed(activeSuggestion)) return;
				externalTargetUrl = externalStore.url;
				popup.classList.add('is-external-store');
				populateSuggestion(externalStore);
				populateExternalTranslation(externalStore);
				setTimeout(() => openPopup(), 500);
				return;
			}

			if (localStorage.getItem(legacySeenKey)) return;

			if (detectedCountryCode && detectedCountryCode !== currentCountryCode && countriesMap[detectedCountryCode]) {
				const suggestedMarket = countriesMap[detectedCountryCode];
				activeSuggestion = 'market:' + detectedCountryCode;
				if (isSuggestionSuppressed(activeSuggestion)) return;

				populateSuggestion(suggestedMarket);
				if (countryInput) countryInput.value = suggestedMarket.iso_code;

				if (languageInput && suggestedMarket.languages && suggestedMarket.languages.length > 0) {
					if (suggestedMarket.languages.includes(detectedLanguageCode)) {
						languageInput.value = detectedLanguageCode;
					} else {
						languageInput.value = suggestedMarket.languages[0];
					}
				}

				setTimeout(() => openPopup(), 500);
			} else {
				localStorage.setItem(legacySeenKey, 'true');
			}
		})
		.catch(error => {
			console.error('Localization fetch error:', error);
		});

	form.addEventListener('submit', (event) => {
		if (externalTargetUrl) {
			event.preventDefault();
			rememberDecision('switch');
			window.location.assign(externalTargetUrl);
			return;
		}
		rememberDecision('switch');
		localStorage.setItem(legacySeenKey, 'true');
	});
});

// Mobile nav: open after gender switch
document.addEventListener('DOMContentLoaded', function () {
	if (localStorage.getItem('open_nav') && window.innerWidth <= 1055) {
		localStorage.removeItem('open_nav');

		const nav = document.querySelector('.header nav');
		const mobileBtn = document.querySelector('.mobile-button');
		const htmlDoc = document.documentElement;

		setTimeout(function () {
			if (nav) {
				nav.classList.add('active');
			}
			if (mobileBtn) {
				mobileBtn.classList.add('active');
			}
			htmlDoc.classList.add('overflow-menu');
		}, 100);
	}

	document.querySelectorAll('.main-category a').forEach(function (link) {
		link.addEventListener('click', function () {
			if (window.innerWidth <= 1055) {
				localStorage.setItem('open_nav', '1');
			}
		});
	});
});

// REYTEL Czech storefront UI localization.
(function () {
	if ((document.documentElement.lang || '').toLowerCase().split('-')[0] !== 'cs') return;

	const exactText = new Map([
		['Write a review', 'Napsat recenzi'],
		['UNLOCK 5% OFF', 'ZÍSKAT SLEVU 5 %'],
		['UNLOCK OFFER', 'ZÍSKAT SLEVU'],
		['No, thanks', 'Ne, děkuji'],
		['Submit', 'Odeslat'],
		['Czechia', 'Česko'],
		['Czechia:', 'Česko:'],
		['Close teaser', 'Zavřít nabídku'],
		['Close dialog', 'Zavřít dialog'],
	]);
	const ariaText = new Map([
		['Previous slide', 'Předchozí snímek'],
		['Next slide', 'Další snímek'],
		['Close teaser', 'Zavřít nabídku'],
		['Close dialog', 'Zavřít dialog'],
	]);

	function localizeElement(element) {
		if (!(element instanceof Element)) return;
		if (/^(SCRIPT|STYLE|TEXTAREA|NOSCRIPT|TEMPLATE)$/.test(element.tagName)) return;

		const ariaLabel = element.getAttribute('aria-label');
		if (ariaText.has(ariaLabel)) element.setAttribute('aria-label', ariaText.get(ariaLabel));

		if (element.children.length === 0) {
			const currentText = element.textContent.trim();
			if (exactText.has(currentText)) element.textContent = exactText.get(currentText);
		}
	}

	function localizeTree(root) {
		if (root instanceof Element) localizeElement(root);
		if (!root.querySelectorAll) return;
		root.querySelectorAll('[aria-label], .jdgm-write-rev-link, .needsclick, title, strong').forEach(localizeElement);
	}

	function startLocalization() {
		localizeTree(document);
		new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === 'attributes') localizeElement(mutation.target);
				if (mutation.type === 'characterData') localizeElement(mutation.target.parentElement);
				mutation.addedNodes.forEach((node) => localizeTree(node));
			});
		}).observe(document.documentElement, { childList: true, subtree: true });
	}

	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startLocalization, { once: true });
	else startLocalization();
})();

// REYTEL Swedish storefront UI localization for third-party widgets and generated accessibility labels.
(function () {
	if ((document.documentElement.lang || '').toLowerCase().split('-')[0] !== 'sv') return;

	const exactText = new Map([
		['JEWELRY THAT SPEAKS FOR YOU', 'SMYCKEN SOM UTTRYCKER DIN STIL'],
		['Unlock your exclusive 5% welcome offer', 'Få ditt exklusiva välkomsterbjudande på 5 %'],
		['UNLOCK 5% OFF', 'FÅ 5 % RABATT'],
		['UNLOCK OFFER', 'AKTIVERA ERBJUDANDET'],
		['No, thanks', 'Nej tack'],
		['Submit', 'Skicka'],
		['Close teaser', 'Stäng erbjudandet'],
		['Close dialog', 'Stäng dialogrutan'],
		['Write a review', 'Skriv en recension'],
		['See all reviews', 'Visa alla recensioner'],
		['Customer Reviews', 'Kundrecensioner'],
		['CUSTOMER REVIEWS', 'KUNDRECENSIONER'],
		['Reviews in Other Languages', 'Recensioner på andra språk'],
		['REVIEWS IN OTHER LANGUAGES', 'RECENSIONER PÅ ANDRA SPRÅK'],
		['Most Recent', 'Senaste'],
		['Highest Rating', 'Högsta betyg'],
		['Lowest Rating', 'Lägsta betyg'],
		['Only Pictures', 'Endast bilder'],
		['Pictures First', 'Bilder först'],
		['Videos First', 'Videor först'],
		['Most Helpful', 'Mest hjälpsamma'],
		['Verified', 'Verifierad'],
		['United States', 'USA'],
		['Read more', 'Läs mer'],
		['Review collected via store invitation', 'Recension insamlad via butiksinbjudan'],
		['Review earned a reward for future purchase', 'Recensionen gav en belöning till ett framtida köp'],
		['Review collected from a store visitor', 'Recension insamlad från en butiksbesökare'],
		['Packaging That Tells a Story', 'Förpackning med en berättelse'],
		['PACKAGING THAT TELLS A STORY', 'FÖRPACKNING MED EN BERÄTTELSE'],
		['Sort by', 'Sortera efter'],
		['FULLSCREEN Form', 'Helskärmsformulär'],
		['Sale', 'Rea'],
		['Chat', 'Chatt'],
		['Previous', 'Föregående'],
		['Next', 'Nästa'],
	]);
	const ariaText = new Map([
		['Previous slide', 'Föregående bild'],
		['Next slide', 'Nästa bild'],
		['Previous page', 'Föregående sida'],
		['Next page', 'Nästa sida'],
		['See all reviews', 'Visa alla recensioner'],
		['Close teaser', 'Stäng erbjudandet'],
		['Close dialog', 'Stäng dialogrutan'],
		['minus', 'Minska antalet'],
		['quantity', 'Antal'],
		['plus', 'Öka antalet'],
	]);
	const altText = new Map([
		['Slider prev', 'Föregående bild'],
		['Slider next', 'Nästa bild'],
		['Previous slide', 'Föregående bild'],
		['Next slide', 'Nästa bild'],
		['Verified Checkmark', 'Verifierad recension'],
		['Search icon', 'Sökikon'],
		['image', 'Bild i recension'],
	]);

	function localizeElement(element) {
		if (!(element instanceof Element)) return;
		if (/^(SCRIPT|STYLE|TEXTAREA|NOSCRIPT|TEMPLATE)$/.test(element.tagName)) return;
		const ariaLabel = element.getAttribute('aria-label');
		if (ariaText.has(ariaLabel)) element.setAttribute('aria-label', ariaText.get(ariaLabel));
		if (/^Average rating is ([0-9.]+) stars$/.test(ariaLabel || '')) element.setAttribute('aria-label', ariaLabel.replace(/^Average rating is ([0-9.]+) stars$/, 'Genomsnittligt betyg: $1 av 5 stjärnor'));
		if (/^(\d+) star review$/.test(ariaLabel || '')) element.setAttribute('aria-label', ariaLabel.replace(/^(\d+) star review$/, 'Recension med $1 stjärnor'));
		const alt = element.getAttribute('alt');
		if (altText.has(alt)) element.setAttribute('alt', altText.get(alt));
		const renderedText = (element.innerText || '').trim();
		if (((element.id || '').startsWith('rich-text-') || element.tagName === 'BUTTON') && exactText.has(renderedText)) {
			element.textContent = exactText.get(renderedText);
			return;
		}
		if (element.children.length === 0) {
			const currentText = element.textContent.trim();
			let replacement = exactText.get(currentText);
			let match = currentText.match(/^(\d+) reviews?$/i);
			if (match) replacement = `${match[1]} ${Number(match[1]) === 1 ? 'recension' : 'recensioner'}`;
			match = currentText.match(/^Based on (\d+) reviews?$/i);
			if (match) replacement = `Baserat på ${match[1]} ${Number(match[1]) === 1 ? 'recension' : 'recensioner'}`;
			match = currentText.match(/^([0-9]+(?:\.[0-9]+)?) out of 5$/i);
			if (match) replacement = `${match[1].replace('.', ',')} av 5`;
			if (element.matches('.jdgm-rev__timestamp') && /^(\d{2})\/(\d{2})\/(\d{4})$/.test(currentText)) replacement = currentText.replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, '$3-$1-$2');
			if (replacement && replacement !== currentText) element.textContent = replacement;
		}
	}

	function localizeTree(root) {
		if (root instanceof Element) localizeElement(root);
		if (!root.querySelectorAll) return;
		root.querySelectorAll('[aria-label], [alt], [id^="rich-text-"], .jdgm-prev-badge__text, .jdgm-write-rev-link, .jdgm-sort-dropdown, .jdgm-sort-dropdown-label, .jdgm-sort-dropdown option, .jdgm-rev-widg__title, .jdgm-rev-widg__summary-average, .jdgm-histogram__clear-filter, .jdgm-rev-widg__section-title, .jdgm-rev__buyer-badge, .jdgm-rev__location, .jdgm-rev__timestamp, .jdgm-rev__body-read-more, .jdgm-rev__transparency-badge, .needsclick, title, strong, button').forEach(localizeElement);
	}

	function startLocalization() {
		localizeTree(document);
		if (!document.getElementById('reytel-sv-widget-localization')) {
			const style = document.createElement('style');
			style.id = 'reytel-sv-widget-localization';
			style.textContent = 'html[lang^="sv"] .jdgm-rev__buyer-badge::before{content:"Verifierad"!important}';
			document.head.appendChild(style);
		}
		new MutationObserver((mutations) => mutations.forEach((mutation) => {
			if (mutation.type === 'attributes') localizeElement(mutation.target);
			if (mutation.type === 'characterData') localizeElement(mutation.target.parentElement);
			mutation.addedNodes.forEach((node) => localizeTree(node));
		})).observe(document.documentElement, { childList: true, subtree: true });
	}

	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startLocalization, { once: true });
	else startLocalization();
})();


// REYTEL Slovak storefront UI localization for generated labels and third-party widgets.
(function () {
	if ((document.documentElement.lang || '').toLowerCase().split('-')[0] !== 'sk') return;

	const exactText = new Map([
		['JEWELRY THAT SPEAKS FOR YOU', 'ŠPERKY, KTORÉ HOVORIA ZA VÁS'],
		['Unlock your exclusive 5% welcome offer', 'Získajte uvítaciu zľavu 5 %'],
		['UNLOCK 5% OFF', 'ZÍSKAŤ ZĽAVU 5 %'],
		['UNLOCK', 'ZÍSKAŤ ZĽAVU'],
		['5 % OFF', 'ZĽAVA 5 %'],
		['UNLOCK OFFER', 'ZÍSKAŤ PONUKU'],
		['No, thanks', 'Nie, ďakujem'],
		['Submit', 'Odoslať'],
		['Close teaser', 'Zavrieť ponuku'],
		['Close dialog', 'Zavrieť okno'],
		['Write a review', 'Napísať recenziu'],
		['Sale', 'Výpredaj'],
		['Sold Out', 'Vypredané'],
		['Sort by', 'Zoradiť podľa'],
		['filters', 'Filtre'],
		['Filters', 'Filtre'],
		['Previous', 'Predchádzajúce'],
		['Next', 'Nasledujúce'],
		['FULLSCREEN Form', 'Formulár na celú obrazovku'],
	]);
	const ariaText = new Map([
		['Previous slide', 'Predchádzajúci obrázok'],
		['Next slide', 'Nasledujúci obrázok'],
		['Previous page', 'Predchádzajúca strana'],
		['Next page', 'Nasledujúca strana'],
		['Close teaser', 'Zavrieť ponuku'],
		['Close dialog', 'Zavrieť okno'],
		['minus', 'Znížiť množstvo'],
		['quantity', 'Množstvo'],
		['plus', 'Zvýšiť množstvo'],
		['Chat', 'Otvoriť chat'],
		['Search', 'Hľadať'],
		['Packaging That Tells a Story', 'Balenie, ktoré rozpráva príbeh'],
		['FULLSCREEN Form', 'Formulár na celú obrazovku'],
	]);
	const altText = new Map([
		['Slider prev', 'Predchádzajúci obrázok'],
		['Slider next', 'Nasledujúci obrázok'],
		['Previous slide', 'Predchádzajúci obrázok'],
		['Next slide', 'Nasledujúci obrázok'],
		['Previous', 'Predchádzajúca strana'],
		['Next', 'Nasledujúca strana'],
		['Search icon', 'Hľadať'],
		['Verified Checkmark', 'Overená recenzia'],
		['Remove', 'Odstrániť'],
	]);

	function localizedAria(value) {
		if (ariaText.has(value)) return ariaText.get(value);
		let match = value.match(/^Average rating is ([0-9]+(?:\.[0-9]+)?) stars?$/i);
		if (match) return 'Priemerné hodnotenie: ' + match[1].replace('.', ',') + ' z 5 hviezdičiek';
		match = value.match(/^([0-9]+(?:\.[0-9]+)?) star review$/i);
		if (match) return 'Recenzia s hodnotením ' + match[1].replace('.', ',') + ' z 5 hviezdičiek';
		match = value.match(/^(\d+)% \((\d+)\) reviews with (\d+) star rating$/i);
		if (match) return match[1] + ' % (' + match[2] + ' recenzií) s hodnotením ' + match[3] + ' hviezdičiek';
		return value;
	}

	function formatEuroValue(value) {
		if (/^\d{1,3}(?:\.\d{3})+$/.test(value)) return value.replace(/\./g, ' ');
		if (/^\d+\.\d{2}$/.test(value)) return value.replace('.', ',');
		return value;
	}

	function localizedText(value) {
		let result = exactText.get(value) || value;
		result = result.replace(/€\s*([0-9][0-9.,]*)/g, function (_match, amount) {
			return formatEuroValue(amount) + ' €';
		});
		result = result.replace(/([0-9])\s*%/g, '$1 %');
		return result;
	}

	function localizeElement(element) {
		if (!(element instanceof Element)) return;
		if (/^(SCRIPT|STYLE|TEXTAREA|NOSCRIPT|TEMPLATE)$/.test(element.tagName)) return;
		const ariaLabel = element.getAttribute('aria-label');
		if (ariaLabel) {
			const replacement = localizedAria(ariaLabel);
			if (replacement !== ariaLabel) element.setAttribute('aria-label', replacement);
		}
		const alt = element.getAttribute('alt');
		if (altText.has(alt)) element.setAttribute('alt', altText.get(alt));
		const renderedText = (element.innerText || '').trim();
		if ((element.id.startsWith('rich-text-') || element.tagName === 'BUTTON') && exactText.has(renderedText)) {
			element.textContent = exactText.get(renderedText);
			return;
		}
		if (element.children.length === 0) {
			const currentText = element.textContent.trim();
			const replacement = localizedText(currentText);
			if (replacement !== currentText) element.textContent = replacement;
			if (element.matches('.jdgm-rev__timestamp') && /^(\d{2})\/(\d{2})\/(\d{4})$/.test(currentText)) {
				element.textContent = currentText.replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, '$2. $1. $3');
			}
		}
	}

	function localizeTextNode(node) {
		if (!(node instanceof Text) || !node.parentElement || node.parentElement.closest('script, style, textarea')) return;
		const replacement = localizedText(node.nodeValue);
		if (replacement !== node.nodeValue) node.nodeValue = replacement;
	}

	function localizeTree(root) {
		if (root instanceof Element) localizeElement(root);
		if (!root.querySelectorAll) return;
		root.querySelectorAll('[aria-label], [alt], [id^="rich-text-"], .jdgm-write-rev-link, .jdgm-sort-dropdown, .jdgm-sort-dropdown-label, .jdgm-sort-dropdown option, .jdgm-rev__timestamp, .needsclick, title, strong, button, .price, .sale-badge, .cart-promo-text').forEach(localizeElement);
		const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
		let node;
		while ((node = walker.nextNode())) localizeTextNode(node);
	}

	function localizeShopifyChat() {
		const root = document.querySelector('shopify-chat')?.shadowRoot;
		if (!root) return false;
		const button = root.querySelector('button[part="activator"]');
		if (button?.getAttribute('aria-label') === 'Chat') button.setAttribute('aria-label', 'Otvoriť chat');
		return true;
	}

	function startLocalization() {
		localizeTree(document);
		setTimeout(localizeShopifyChat, 2500);
		new MutationObserver(function (mutations) {
			mutations.forEach(function (mutation) {
				if (mutation.type === 'attributes') localizeElement(mutation.target);
				if (mutation.type === 'characterData') localizeTextNode(mutation.target);
				mutation.addedNodes.forEach(function (node) { localizeTree(node); });
			});
		}).observe(document.documentElement, { childList: true, subtree: true });
	}

	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startLocalization, { once: true });
	else startLocalization();
})();

// REYTEL French storefront UI localization for third-party widgets and generated accessibility labels.
(function () {
	if ((document.documentElement.lang || '').toLowerCase().split('-')[0] !== 'fr') return;

	const exactText = new Map([
		['JEWELRY THAT SPEAKS FOR YOU', 'DES BIJOUX QUI VOUS RESSEMBLENT'],
		['Unlock your exclusive 5% welcome offer', 'Profitez de votre offre de bienvenue exclusive de 5 %'],
		['UNLOCK 5% OFF', 'OBTENEZ 5 % DE RÉDUCTION'],
		['UNLOCK OFFER', 'PROFITER DE L’OFFRE'],
		['No, thanks', 'Non merci'],
		['Submit', 'Valider'],
		['Close teaser', 'Fermer l’offre'],
		['Close dialog', 'Fermer la fenêtre'],
		['Write a review', 'Écrire un avis'],
		['Sort by', 'Trier par'],
		['FULLSCREEN Form', 'Formulaire plein écran'],
		['United States', 'États-Unis'],
		['Sale', 'Soldes'],
		['Chat', 'Discussion'],
		['Previous', 'Précédent'],
		['Next', 'Suivant'],
	]);
	const ariaText = new Map([
		['Previous slide', 'Diapositive précédente'],
		['Next slide', 'Diapositive suivante'],
		['Previous page', 'Page précédente'],
		['Next page', 'Page suivante'],
		['Close teaser', 'Fermer l’offre'],
		['Close dialog', 'Fermer la fenêtre'],
		['minus', 'Réduire la quantité'],
		['quantity', 'Quantité'],
		['plus', 'Augmenter la quantité'],
		['Chat', 'Discussion'],
		['FULLSCREEN Form', 'Formulaire plein écran'],
	]);
	const altText = new Map([
		['Slider prev', 'Diapositive précédente'],
		['Slider next', 'Diapositive suivante'],
		['Previous slide', 'Diapositive précédente'],
		['Next slide', 'Diapositive suivante'],
		['Verified Checkmark', 'Avis vérifié'],
		['US', 'États-Unis'],
		['Search icon', 'Icône de recherche'],
		['image', 'Image de l’avis'],
	]);

	function localizeElement(element) {
		if (!(element instanceof Element)) return;
		if (/^(SCRIPT|STYLE|TEXTAREA|NOSCRIPT|TEMPLATE)$/.test(element.tagName)) return;
		const ariaLabel = element.getAttribute('aria-label');
		if (ariaText.has(ariaLabel)) element.setAttribute('aria-label', ariaText.get(ariaLabel));
		if (/^Average rating is ([0-9.]+) stars$/.test(ariaLabel || '')) element.setAttribute('aria-label', ariaLabel.replace(/^Average rating is ([0-9.]+) stars$/, 'La note moyenne est de $1 étoiles'));
		if (/^(\d+)% \((\d+)\) reviews with (\d+) star rating$/.test(ariaLabel || '')) element.setAttribute('aria-label', ariaLabel.replace(/^(\d+)% \((\d+)\) reviews with (\d+) star rating$/, '$1 % ($2) avis avec une note de $3 étoiles'));
		if (/^(\d+) star review$/.test(ariaLabel || '')) element.setAttribute('aria-label', ariaLabel.replace(/^(\d+) star review$/, 'Avis noté $1 étoiles'));
		const alt = element.getAttribute('alt');
		if (altText.has(alt)) element.setAttribute('alt', altText.get(alt));
		const renderedText = (element.innerText || '').trim();
		if ((element.id.startsWith('rich-text-') || element.tagName === 'BUTTON') && exactText.has(renderedText)) {
			element.textContent = exactText.get(renderedText);
			return;
		}
		if (element.children.length === 0) {
			const currentText = element.textContent.trim();
			if (exactText.has(currentText)) element.textContent = exactText.get(currentText);
			if (element.matches('.jdgm-rev__timestamp') && /^(\d{2})\/(\d{2})\/(\d{4})$/.test(currentText)) {
				element.textContent = currentText.replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, '$2/$1/$3');
			}
		}
	}

	function localizeTree(root) {
		if (root instanceof Element) localizeElement(root);
		if (!root.querySelectorAll) return;
		root.querySelectorAll('[aria-label], [alt], [id^="rich-text-"], .jdgm-write-rev-link, .jdgm-sort-dropdown, .jdgm-sort-dropdown-label, .jdgm-sort-dropdown option, .jdgm-rev__location, .jdgm-rev__timestamp, .needsclick, title, strong, button').forEach(localizeElement);
	}

	function localizeShopifyChat() {
		const root = document.querySelector('shopify-chat')?.shadowRoot;
		if (!root) return false;
		const button = root.querySelector('button[part="activator"]');
		const label = root.querySelector('.activator__label');
		if (button?.getAttribute('aria-label') === 'Chat') button.setAttribute('aria-label', 'Discussion');
		if (label?.textContent.trim() === 'Chat') label.textContent = 'Discussion';
		return true;
	}

	function startLocalization() {
		localizeTree(document);
		setTimeout(localizeShopifyChat, 2500);
		new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === 'attributes') localizeElement(mutation.target);
				if (mutation.type === 'characterData') localizeElement(mutation.target.parentElement);
				mutation.addedNodes.forEach((node) => localizeTree(node));
			});
		}).observe(document.documentElement, { childList: true, subtree: true });
	}

	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startLocalization, { once: true });
	else startLocalization();
})();

// REYTEL German storefront UI localization for generated labels and third-party widgets.
(function () {
	if ((document.documentElement.lang || '').toLowerCase().split('-')[0] !== 'de') return;

	const exactText = new Map([
		['JEWELRY THAT SPEAKS FOR YOU', 'SCHMUCK, DER FÜR SIE SPRICHT'],
		['Unlock your exclusive 5% welcome offer', 'Sichern Sie sich 5 % Willkommensrabatt'],
		['UNLOCK 5% OFF', '5 % RABATT SICHERN'],
		['UNLOCK', '5 % RABATT'],
		['5 % OFF', 'SICHERN'],
		['UNLOCK OFFER', 'ANGEBOT SICHERN'],
		['No, thanks', 'Nein, danke'],
		['Submit', 'Absenden'],
		['Close teaser', 'Angebot schließen'],
		['Close dialog', 'Dialog schließen'],
		['Write a review', 'Bewertung schreiben'],
		['Sort by', 'Sortieren nach'],
		['Previous', 'Zurück'],
		['Next', 'Weiter'],
	]);
	const ariaText = new Map([
		['Previous slide', 'Vorherige Folie'],
		['Next slide', 'Nächste Folie'],
		['Previous page', 'Vorherige Seite'],
		['Next page', 'Nächste Seite'],
		['Close teaser', 'Angebot schließen'],
		['Close dialog', 'Dialog schließen'],
		['minus', 'Menge verringern'],
		['quantity', 'Menge'],
		['plus', 'Menge erhöhen'],
		['Chat', 'Chat öffnen'],
	]);
	const altText = new Map([
		['Slider prev', 'Vorheriges Bild'],
		['Slider next', 'Nächstes Bild'],
		['Previous slide', 'Vorheriges Bild'],
		['Next slide', 'Nächstes Bild'],
		['Previous', 'Vorherige Seite'],
		['Next', 'Nächste Seite'],
	]);

	function localizedAria(value) {
		if (ariaText.has(value)) return ariaText.get(value);
		let match = value.match(/^Average rating is ([0-9]+(?:\.[0-9]+)?) stars?$/i);
		if (match) return 'Durchschnittliche Bewertung: ' + match[1].replace('.', ',') + ' von 5 Sternen';
		match = value.match(/^([0-9]+(?:\.[0-9]+)?) star review$/i);
		if (match) return 'Bewertung mit ' + match[1].replace('.', ',') + ' von 5 Sternen';
		return value;
	}

	function localizedText(value) {
		let result = exactText.get(value) || value;
		result = result.replace(/€\s*([0-9][0-9.,]*)/g, '$1 €');
		result = result.replace(/([0-9])\s*%/g, '$1 %');
		return result;
	}

	function localizeElement(element) {
		if (!(element instanceof Element)) return;
		if (/^(SCRIPT|STYLE|TEXTAREA|NOSCRIPT|TEMPLATE)$/.test(element.tagName)) return;
		const ariaLabel = element.getAttribute('aria-label');
		if (ariaLabel) {
			const replacement = localizedAria(ariaLabel);
			if (replacement !== ariaLabel) element.setAttribute('aria-label', replacement);
		}
		const alt = element.getAttribute('alt');
		if (altText.has(alt)) element.setAttribute('alt', altText.get(alt));
		if (element.children.length === 0) {
			const currentText = element.textContent.trim();
			const replacement = localizedText(currentText);
			if (replacement !== currentText) element.textContent = replacement;
		}
	}

	function localizeTree(root) {
		if (root instanceof Element) localizeElement(root);
		if (!root.querySelectorAll) return;
		root.querySelectorAll('[aria-label], [alt], .jdgm-write-rev-link, .jdgm-sort-dropdown, .jdgm-sort-dropdown option, .needsclick, title, strong, button, .price, .sale-badge, .cart-promo-text').forEach(localizeElement);
	}

	function startLocalization() {
		localizeTree(document);
		new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === 'attributes') localizeElement(mutation.target);
				if (mutation.type === 'characterData') localizeElement(mutation.target.parentElement);
				mutation.addedNodes.forEach((node) => localizeTree(node));
			});
		}).observe(document.documentElement, { childList: true, subtree: true });
	}

	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startLocalization, { once: true });
	else startLocalization();
})();

// REYTEL Romanian storefront UI localization for third-party widgets and generated accessibility labels.
(function () {
	if ((document.documentElement.lang || '').toLowerCase().split('-')[0] !== 'ro') return;

	const exactText = new Map([
		['JEWELRY THAT SPEAKS FOR YOU', 'BIJUTERII CARE TE REPREZINTĂ'],
		['Unlock your exclusive 5% welcome offer', 'Primește oferta exclusivă de bun venit de 5%'],
		['UNLOCK 5% OFF', 'OBȚINE 5% REDUCERE'],
		['UNLOCK OFFER', 'ACTIVEAZĂ OFERTA'],
		['No, thanks', 'Nu, mulțumesc'],
		['Submit', 'Trimite'],
		['Close teaser', 'Închide oferta'],
		['Close dialog', 'Închide fereastra'],
		['Write a review', 'Scrie o recenzie'],
		['See all reviews', 'Vezi toate recenziile'],
		['Customer Reviews', 'Recenziile clienților'],
		['Reviews in Other Languages', 'Recenzii în alte limbi'],
		['Most Recent', 'Cele mai recente'],
		['Highest Rating', 'Cel mai mare rating'],
		['Lowest Rating', 'Cel mai mic rating'],
		['Only Pictures', 'Doar cu imagini'],
		['Pictures First', 'Mai întâi cele cu imagini'],
		['Videos First', 'Mai întâi cele cu videoclipuri'],
		['Most Helpful', 'Cele mai utile'],
		['Verified', 'Verificat'],
		['United States', 'Statele Unite'],
		['Read more', 'Citește mai mult'],
		['Review collected via store invitation', 'Recenzie colectată prin invitația magazinului'],
		['Review earned a reward for future purchase', 'Recenzia a primit o recompensă pentru o achiziție viitoare'],
		['Review collected from a store visitor', 'Recenzie colectată de la un vizitator al magazinului'],
		['Packaging That Tells a Story', 'Ambalaj cu o poveste'],
		['Sort by', 'Sortează după'],
		['FULLSCREEN Form', 'Formular pe ecran complet'],
		['Sale', 'Reduceri'],
		['Chat', 'Chat'],
		['Previous', 'Anterior'],
		['Next', 'Următorul'],
	]);
	const ariaText = new Map([
		['Previous slide', 'Slide-ul anterior'],
		['Next slide', 'Slide-ul următor'],
		['Previous page', 'Pagina anterioară'],
		['Next page', 'Pagina următoare'],
		['See all reviews', 'Vezi toate recenziile'],
		['Close teaser', 'Închide oferta'],
		['Close dialog', 'Închide fereastra'],
		['minus', 'Redu cantitatea'],
		['quantity', 'Cantitate'],
		['plus', 'Mărește cantitatea'],
	]);
	const altText = new Map([
		['Slider prev', 'Imaginea anterioară'],
		['Slider next', 'Imaginea următoare'],
		['Previous slide', 'Imaginea anterioară'],
		['Next slide', 'Imaginea următoare'],
		['Verified Checkmark', 'Recenzie verificată'],
		['Search icon', 'Pictogramă de căutare'],
		['image', 'Imagine din recenzie'],
	]);

	function localizeElement(element) {
		if (!(element instanceof Element)) return;
		if (/^(SCRIPT|STYLE|TEXTAREA|NOSCRIPT|TEMPLATE)$/.test(element.tagName)) return;
		const ariaLabel = element.getAttribute('aria-label');
		if (ariaText.has(ariaLabel)) element.setAttribute('aria-label', ariaText.get(ariaLabel));
		if (/^Average rating is ([0-9.]+) stars$/.test(ariaLabel || '')) element.setAttribute('aria-label', ariaLabel.replace(/^Average rating is ([0-9.]+) stars$/, 'Evaluare medie: $1 din 5 stele'));
		if (/^(\d+) star review$/.test(ariaLabel || '')) element.setAttribute('aria-label', ariaLabel.replace(/^(\d+) star review$/, 'Recenzie de $1 stele'));
		const alt = element.getAttribute('alt');
		if (altText.has(alt)) element.setAttribute('alt', altText.get(alt));
		const renderedText = (element.innerText || '').trim();
		if (((element.id || '').startsWith('rich-text-') || element.tagName === 'BUTTON') && exactText.has(renderedText)) {
			element.textContent = exactText.get(renderedText);
			return;
		}
		if (element.children.length === 0) {
			const currentText = element.textContent.trim();
			let replacement = exactText.get(currentText);
			let match = currentText.match(/^(\d+) reviews?$/i);
			if (match) replacement = `${match[1]} ${Number(match[1]) === 1 ? 'recenzie' : 'recenzii'}`;
			match = currentText.match(/^Based on (\d+) reviews?$/i);
			if (match) replacement = `Pe baza a ${match[1]} ${Number(match[1]) === 1 ? 'recenzie' : 'recenzii'}`;
			match = currentText.match(/^([0-9]+(?:\.[0-9]+)?) out of 5$/i);
			if (match) replacement = `${match[1].replace('.', ',')} din 5`;
			if (element.matches('.jdgm-rev__timestamp') && /^(\d{2})\/(\d{2})\/(\d{4})$/.test(currentText)) replacement = currentText.replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, '$2.$1.$3');
			if (replacement && replacement !== currentText) element.textContent = replacement;
		}
	}

	function localizeTree(root) {
		if (root instanceof Element) localizeElement(root);
		if (!root.querySelectorAll) return;
		root.querySelectorAll('[aria-label], [alt], [id^="rich-text-"], .jdgm-prev-badge__text, .jdgm-write-rev-link, .jdgm-sort-dropdown, .jdgm-sort-dropdown-label, .jdgm-sort-dropdown option, .jdgm-rev-widg__title, .jdgm-rev-widg__summary-average, .jdgm-histogram__clear-filter, .jdgm-rev-widg__section-title, .jdgm-rev__buyer-badge, .jdgm-rev__location, .jdgm-rev__timestamp, .jdgm-rev__body-read-more, .jdgm-rev__transparency-badge, .needsclick, title, strong, button').forEach(localizeElement);
	}

	function startLocalization() {
		localizeTree(document);
		if (!document.getElementById('reytel-ro-widget-localization')) {
			const style = document.createElement('style');
			style.id = 'reytel-ro-widget-localization';
			style.textContent = 'html[lang^="ro"] .jdgm-rev__buyer-badge::before{content:"Verificat"!important}';
			document.head.appendChild(style);
		}
		new MutationObserver((mutations) => mutations.forEach((mutation) => {
			if (mutation.type === 'attributes') localizeElement(mutation.target);
			if (mutation.type === 'characterData') localizeElement(mutation.target.parentElement);
			mutation.addedNodes.forEach((node) => localizeTree(node));
		})).observe(document.documentElement, { childList: true, subtree: true });
	}

	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startLocalization, { once: true });
	else startLocalization();
})();

// REYTEL Dutch storefront UI localization for third-party widgets and generated accessibility labels.
(function () {
  if ((document.documentElement.lang || '').toLowerCase().split('-')[0] !== 'nl') return;
  const exactText = new Map([
    ['JEWELRY THAT SPEAKS FOR YOU', 'SIERADEN DIE VOOR JE SPREKEN'],
    ['Unlock your exclusive 5% welcome offer', 'Ontvang je exclusieve welkomstkorting van 5%'],
    ['UNLOCK 5% OFF', 'ONTVANG 5% KORTING'], ['UNLOCK OFFER', 'ACTIVEER AANBOD'],
    ['No, thanks', 'Nee, bedankt'], ['Submit', 'Versturen'],
    ['Close teaser', 'Aanbieding sluiten'], ['Close dialog', 'Venster sluiten'],
    ['Write a review', 'Schrijf een review'], ['Sort by', 'Sorteren op'],
    ['FULLSCREEN Form', 'Formulier op volledig scherm'], ['Sale', 'SALE'],
    ['Chat', 'Chat'], ['Previous', 'Vorige'], ['Next', 'Volgende'],
  ]);
  const ariaText = new Map([
    ['Previous slide', 'Vorige slide'], ['Next slide', 'Volgende slide'],
    ['Previous page', 'Vorige pagina'], ['Next page', 'Volgende pagina'],
    ['Close teaser', 'Aanbieding sluiten'], ['Close dialog', 'Venster sluiten'],
    ['minus', 'Aantal verlagen'], ['quantity', 'Aantal'], ['plus', 'Aantal verhogen'],
  ]);
  const altText = new Map([
    ['Slider prev', 'Vorige afbeelding'], ['Slider next', 'Volgende afbeelding'],
    ['Previous slide', 'Vorige afbeelding'], ['Next slide', 'Volgende afbeelding'],
    ['Verified Checkmark', 'Geverifieerde review'], ['Search icon', 'Zoekpictogram'],
    ['image', 'Afbeelding uit review'],
  ]);
  function localizeElement(element) {
    if (!(element instanceof Element)) return;
    if (/^(SCRIPT|STYLE|TEXTAREA|NOSCRIPT|TEMPLATE)$/.test(element.tagName)) return;
    const ariaLabel = element.getAttribute('aria-label');
    if (ariaText.has(ariaLabel)) element.setAttribute('aria-label', ariaText.get(ariaLabel));
    if (/^Average rating is ([0-9.]+) stars$/.test(ariaLabel || '')) element.setAttribute('aria-label', ariaLabel.replace(/^Average rating is ([0-9.]+) stars$/, 'Gemiddelde beoordeling: $1 van 5 sterren'));
    if (/^(\d+) star review$/.test(ariaLabel || '')) element.setAttribute('aria-label', ariaLabel.replace(/^(\d+) star review$/, 'Review van $1 sterren'));
    const alt = element.getAttribute('alt');
    if (altText.has(alt)) element.setAttribute('alt', altText.get(alt));
    if (element.children.length === 0) {
      const currentText = element.textContent.trim();
      if (exactText.has(currentText)) element.textContent = exactText.get(currentText);
    }
  }
  function localizeTextNode(node) {
    if (!(node instanceof Text) || node.parentElement?.closest('script, style, textarea')) return;
    const original = node.nodeValue.replace(/\s+/g, ' ').trim();
    if (exactText.has(original)) node.nodeValue = exactText.get(original);
  }
  function localizeTree(root) {
    if (root instanceof Text) { localizeTextNode(root); return; }
    if (root instanceof Element) localizeElement(root);
    if (!root.querySelectorAll) return;
    root.querySelectorAll('[aria-label], [alt], [id^="rich-text-"], .jdgm-write-rev-link, .jdgm-sort-dropdown, .jdgm-sort-dropdown-label, .jdgm-sort-dropdown option, .needsclick, title, strong, button').forEach(localizeElement);
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let textNode;
    while ((textNode = walker.nextNode())) localizeTextNode(textNode);
  }
  function startLocalization() {
    localizeTree(document);
    new MutationObserver((mutations) => mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') localizeElement(mutation.target);
      if (mutation.type === 'characterData') localizeTextNode(mutation.target);
      mutation.addedNodes.forEach((node) => localizeTree(node));
    })).observe(document.documentElement, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startLocalization, { once: true });
  else startLocalization();
})();

/* REYTEL_NL_VISIBLE_COPY_V2_START */
(() => {
  const isDutchStorefront = () => /^\/nl-nl(?:\/|$)/.test(window.location.pathname) || document.documentElement.lang?.toLowerCase().startsWith('nl');
  if (!isDutchStorefront()) return;
  const overflowStyle = document.createElement('style');
  overflowStyle.id = 'reytel-nl-overflow-guard';
  overflowStyle.textContent = 'html, body { max-width: 100%; overflow-x: clip; }';
  document.head.appendChild(overflowStyle);
  const replacements = new Map([
    ['Shop now', 'Nu shoppen'],
    ['shop now', 'Nu shoppen'],
    ['shop nu', 'Nu shoppen'],
    ['Shop collection', 'Bekijk collectie'],
    ['shop collection', 'Bekijk collectie'],
    ['Engravable', 'Graveerbaar'],
    ['From', 'Vanaf'],
    ['Courage adorns', 'Moed siert'],
    ['Subscriberen', 'Inschrijven'],
    ['subscriberen', 'Inschrijven'],
    ['Vervoer en betalingen', 'Verzending en betaling'],
    ['Huis', 'Home'],
    ['SALE', 'ACTIE'],
    ['EARRINGS', 'OORBELLEN'],
    ['Earrings', 'Oorbellen'],
    ['NECKLACES', 'KETTINGEN'],
    ['Necklaces', 'Kettingen'],
    ['RINGS', 'RINGEN'],
    ['Rings', 'Ringen'],
    ['BRACELETS', 'ARMBANDEN'],
    ['Bracelets', 'Armbanden'],
    ['CUFFS', 'OORMANCHETTEN'],
    ['Cuffs', 'Oormanchetten'],
    ['ADD ENGRAVING', 'GRAVURE TOEVOEGEN'],
    ['Add engraving', 'Gravure toevoegen'],
    ['ADD TO BAG', 'IN WINKELWAGEN'],
    ['Add to bag', 'In winkelwagen'],
    ['Handcrafted in Europe', 'Handgemaakt in Europa'],
    ['2-Year Warranty', '2 jaar garantie'],
    ['14-Day Return Policy', '14 dagen retourrecht'],
    ['CARE', 'ONDERHOUD'],
    ['WARRANTY', 'GARANTIE'],
    ['SHIPPING & RETURNS', 'VERZENDING EN RETOURNEREN'],
    ['BUY THIS PENDANT AND GET 30% OFF ANY CHAIN', 'KOOP DEZE HANGER EN KRIJG 30% KORTING OP EEN KETTING'],
    ['Buy this pendant and get 30% off any chain', 'Koop deze hanger en krijg 30% korting op een ketting'],
    ['PACKAGING THAT TELLS A STORY', 'VERPAKKING MET EEN VERHAAL'],
    ['Packaging That Tells a Story', 'Verpakking met een verhaal'],
    ['CUSTOMER REVIEWS', 'KLANTBEOORDELINGEN'],
    ['Customer Reviews', 'Klantbeoordelingen'],
    ['YOU MAY ALSO LIKE', 'DIT VIND JE MISSCHIEN OOK LEUK'],
    ['You may also like', 'Dit vind je misschien ook leuk'],
    ['Add to wishlist', 'Toevoegen aan verlanglijst'],
    ['Material:', 'Materiaal:'],
    ['14k Yellow gold', '14k geelgoud'],
    ['14K Yellow Gold', '14K geelgoud'],
    ['14K White Gold', '14K witgoud'],
    ['14K Rose Gold', '14K roségoud'],
    ['925 Sterling Silver', '925 sterling zilver'],
    ['Chains', 'Kettingen'],
    ['Wipe your jewelry with a', 'Neem je sieraden af met een '],
    ['soft cloth', 'zachte doek'],
    ['after wearing it.', ' na het dragen.'],
    ['Avoid contact with', 'Vermijd contact met '],
    ['perfumes, chemicals, and chlorinated water', 'parfum, chemicaliën en chloorwater'],
    ['Store your jewelry in a', 'Bewaar je sieraden op een '],
    ['dry place', 'droge plek'],
    [', preferably in a jewelry box or pouch.', ', bij voorkeur in een sieradendoos of zakje.'],
    ['If the silver tarnishes, use a', 'Als het zilver verkleurt, gebruik dan een '],
    ['special silver polishing cloth', 'speciale zilverpoetsdoek'],
    ['to restore its shine.', ' om de glans te herstellen.'],
    ['We provide a', 'Wij bieden '],
    ['2-year warranty', '2 jaar garantie'],
    ['on all jewelry.', ' op alle sieraden.'],
    ['The warranty covers', 'De garantie dekt '],
    ['manufacturing defects', 'fabricagefouten'],
    [', but does not apply to damage caused by misuse, accidental breakage, or normal wear and tear.', ', maar geldt niet voor schade door verkeerd gebruik, onopzettelijke breuk of normale slijtage.'],
    ['You can return or exchange your item within', 'Je kunt je artikel binnen '],
    ['14 days', '14 dagen'],
    ['of receiving it.', ' na ontvangst retourneren of omruilen.'],
    ['The item must be', 'Het artikel moet '],
    ['unused, undamaged, and in its original packaging', 'ongebruikt en onbeschadigd zijn en in de originele verpakking zitten'],
    ['Personalized items (with engraving) are non-refundable and cannot be exchanged.', 'Gepersonaliseerde artikelen (met gravure) kunnen niet worden geretourneerd of omgeruild.'],
    ['If you need a different size, we can exchange it, but the shipping cost is covered by the customer.', 'Heb je een andere maat nodig, dan kunnen we het artikel omruilen; de verzendkosten zijn voor de klant.'],
    ['Be the first to write a review', 'Schrijf als eerste een beoordeling'],
    ['JUST SEEN', 'RECENT BEKEKEN'],
    ['Just seen', 'Recent bekeken'],
    ['BE PART OF OUR', 'WORD DEEL VAN ONS'],
    ['Be Part Of OUR', 'Word deel van ons'],
    ["REYTEL' – THE ART OF HIGHLIGHTING", "REYTEL' – DE KUNST OM"],
    ["REYTEL' – The Art of Highlighting", "REYTEL' – De kunst om"],
    ['YOUR UNIQUENESS', 'JOUW UNIEKHEID TE BENADRUKKEN'],
    ['Your Uniqueness', 'jouw uniekheid te benadrukken'],
    ['Handcrafted sterling silver jewelry.', 'Handgemaakte sieraden van sterling zilver.'],
    ['Despite the challenges of war in Ukraine, we remain committed to our craft. Our production continues at the highest level, while our warehouses in Poland ensure safe, reliable, and timely delivery of your orders.', 'Ondanks de oorlog in Oekraïne blijven we ons vakmanschap trouw. Onze productie gaat door op het hoogste niveau, terwijl onze magazijnen in Polen zorgen voor een veilige, betrouwbare en tijdige levering van je bestelling.'],
    ['Despite the challenges of war in Ukraine, we continue to create and develop our jewelry with the same dedication and precision. Our production remains in Ukraine, while our warehouses in Europe ensure safe, reliable, and timely delivery worldwide.', 'Ondanks de oorlog in Oekraïne blijven we onze sieraden met dezelfde toewijding en precisie ontwerpen en vervaardigen. Onze productie blijft in Oekraïne, terwijl onze magazijnen in Europa zorgen voor een veilige, betrouwbare en tijdige levering wereldwijd.'],
    ['We take pride in our heritage and are grateful to share our artistry with the world.', 'We zijn trots op ons erfgoed en dankbaar dat we ons vakmanschap met de wereld kunnen delen.'],
    ["We are proud to share the aesthetics and craftsmanship of REYTEL' WOMAN with women around the world.", "We delen de esthetiek en het vakmanschap van REYTEL' WOMAN met trots met vrouwen over de hele wereld."],
    ["REYTEL' WOMAN is meer dan sieraden het is een handgemaakte uitdrukking van stijl, evenwicht en individualiteit. Ons team van bekwame juweliers en ontwerpers creëert elk stuk вручну, waarbij strakke lijnen, geometrische vormen en nauwkeurig vakmanschap worden gecombineerd. Elk item draagt het ondertekeningsmerk REYTEL' als een symbool van authenticiteit en kwaliteit.", "REYTEL' WOMAN is meer dan sieraden: het is een handgemaakte uitdrukking van stijl, balans en individualiteit. Ons team van ervaren juweliers en ontwerpers vervaardigt elk stuk met de hand en combineert strakke lijnen, geometrische vormen en nauwkeurig vakmanschap. Elk sieraad draagt het kenmerk van REYTEL' als symbool van authenticiteit en kwaliteit."],
    ['AFFILIATE PROGRAM', 'AFFILIATEPROGRAMMA'],
    ['Affiliate Program', 'Affiliateprogramma'],
    ['JOIN NOW', 'DOE NU MEE'],
    ['Join now', 'Doe nu mee'],
    ['Neem deel aan ons affiliate programma en krijg 15% commissie op elke bestellingSchrijf je aan en begin vandaag te verdienen!', 'Neem deel aan ons affiliateprogramma en ontvang 15% commissie op elke bestelling. Schrijf je in en begin vandaag met verdienen!'],
    ['Neem deel aan ons affiliate programma en krijg', 'Neem deel aan ons affiliateprogramma en ontvang '],
    ['Schrijf je aan en begin vandaag te verdienen!', '. Schrijf je in en begin vandaag nog met verdienen!'],
    ['Tracking links en een bericht op je blog of media', 'Deel trackinglinks via je blog of sociale media'],
    ['15% op elke bestelling.', 'Ontvang 15% commissie op elke bestelling.'],
    ['WHY PARTNER WITH REYTEL?', 'WAAROM SAMENWERKEN MET REYTEL?'],
    ['Why Partner with REYTEL?', 'Waarom samenwerken met REYTEL?'],
    ['REYTEL is a growing men’s jewelry brand offering handcrafted pieces in small batches with truly unique designs. Our dedication to quality and detail is matched by our responsive customer support, resulting in a very low return rate.', 'REYTEL is een groeiend sieradenmerk voor heren. We maken unieke ontwerpen met de hand in kleine oplages. Onze aandacht voor kwaliteit en detail, samen met een betrokken klantenservice, zorgt voor een zeer laag retourpercentage.'],
    ['As a young brand, we’re focused on delivering outstanding value — combining premium materials, fair prices, and the option for custom engraving and sizing.', 'Als jong merk bieden we uitzonderlijke waarde door hoogwaardige materialen en eerlijke prijzen te combineren met gravure op maat en verschillende maatopties.'],
    ['With a 15% commission on every order, REYTEL’s affiliate program is a rare opportunity to promote products people love — and get rewarded generously.', 'Met 15% commissie op elke bestelling biedt het affiliateprogramma van REYTEL een bijzondere kans om geliefde producten te promoten en daar ruim voor beloond te worden.'],
    ['With a', 'Met een '],
    ['15% commission on every order', 'commissie van 15% op elke bestelling'],
    [', REYTEL’s affiliate program is a rare opportunity to promote products people love — and get rewarded generously.', ' biedt het affiliateprogramma van REYTEL een bijzondere kans om geliefde producten te promoten en daar ruim voor beloond te worden.'],
    ['LET’S GROW TOGETHER', 'LATEN WE SAMEN GROEIEN'],
    ['Let’s Grow Together', 'Laten we samen groeien'],
    ['We’re open to custom partnership terms for affiliates, content creators, and influencers. Whether you have a large following or a niche audience — let’s find a format that works best for both sides.', 'We staan open voor afspraken op maat met affiliates, contentmakers en influencers. Of je nu een groot bereik of een nichepubliek hebt: samen vinden we een vorm die voor beide partijen werkt.'],
    ['We also provide our partners with access to high-quality photo and video assets you can use in your promotions, making it easy to create compelling content from day one.', 'Partners krijgen toegang tot hoogwaardige foto- en videomaterialen voor hun promoties, zodat je vanaf de eerste dag overtuigende content kunt maken.'],
    ['We also provide our partners with access to', 'Daarnaast bieden we onze partners toegang tot '],
    ['high-quality photo and video assets', 'hoogwaardige foto- en videomaterialen'],
    ['you can use in your promotions, making it easy to create compelling content from day one.', ' die je voor promoties kunt gebruiken, zodat je vanaf de eerste dag overtuigende content kunt maken.'],
    ['Je kunt contact met ons opnemen via het onderstaande formulier, of contact opnemen via live chat of e-mail op info@reytel.com we zijn altijd hier om te helpen.', 'Neem contact met ons op via het formulier hieronder, via de livechat of per e-mail op info@reytel.com. We helpen je graag.'],
    ['BEKIJK REVIEWS', 'BEKIJK BEOORDELINGEN'],
    ['Customer reviews', 'Klantbeoordelingen'],
    ['Schrijf een review', 'Schrijf een beoordeling'],
    ['Sort reviews by', 'Sorteer beoordelingen op'],
    ['Review for', 'Beoordeling voor'],
    ['Reviews in Other Languages', 'Beoordelingen in andere talen'],
    ['Review written in Shop App', 'Beoordeling geschreven in de Shop-app'],
    ['Google-reviews', 'Google-beoordelingen'],
    ['Most Recent', 'Meest recent'],
    ['Highest Rating', 'Hoogste beoordeling'],
    ['Lowest Rating', 'Laagste beoordeling'],
    ['Only Pictures', "Alleen foto's"],
    ['Pictures First', "Foto's eerst"],
    ['Videos First', "Video's eerst"],
    ['Most Helpful', 'Nuttigst'],
    ['United States', 'Verenigde Staten'],
    ['Most recent', 'Meest recent'],
    ['Highest rating', 'Hoogste beoordeling'],
    ['Lowest rating', 'Laagste beoordeling'],
    ['Only pictures', "Alleen foto's"],
    ['Pictures first', "Foto's eerst"],
    ['Videos first', "Video's eerst"],
    ['Most helpful', 'Nuttigst'],
    ['Verified', 'Geverifieerd'],
    ['More', 'Meer'],
    ['Thumbnail Navigation', 'Miniatuurnavigatie'],
    ['Bekijk reviews', 'Bekijk beoordelingen'],
    ['Choose 2 eligible silver pieces and save 15% total; choose 3 or more and save 20% total.', 'Kies 2 deelnemende zilveren sieraden en ontvang in totaal 15% korting; kies 3 of meer en ontvang in totaal 20% korting.'],
    ['MORE JEWELRY. MORE STYLE.', 'MEER SIERADEN. MEER STIJL.'],
    ['How it works:', 'Zo werkt het:'],
    ['How It Works:', 'Zo werkt het:'],
    ['How it works', 'Zo werkt het'],
    ['How It Works', 'Zo werkt het'],
    ['– The offer applies to eligible women’s silver jewelry already marked 10% off.', '– De aanbieding geldt voor deelnemende zilveren damessieraden die al met 10% zijn afgeprijsd.'],
    ['– 1 piece keeps its 10% discount.', '– 1 sieraad behoudt 10% korting.'],
    ['– 2 eligible silver pieces receive a total 15% discount from the original prices.', '– 2 deelnemende zilveren sieraden krijgen in totaal 15% korting op de oorspronkelijke prijzen.'],
    ['– 3 or more eligible silver pieces receive a total 20% discount from the original prices.', '– 3 of meer deelnemende zilveren sieraden krijgen in totaal 20% korting op de oorspronkelijke prijzen.'],
    ['– The discount applies automatically at checkout; no code is required.', '– De korting wordt automatisch toegepast bij het afrekenen; er is geen code nodig.'],
    ['– Items already discounted by 30% and items without an original compare-at price are not eligible.', '– Artikelen die al met 30% zijn afgeprijsd en artikelen zonder oorspronkelijke vergelijkingsprijs komen niet in aanmerking.'],
    ['Returns:', 'Retourneren:'],
    ['If part of an order is returned, the discount and refund may be recalculated based on the number of eligible pieces remaining.', 'Als een deel van een bestelling wordt geretourneerd, kunnen de korting en terugbetaling opnieuw worden berekend op basis van het aantal deelnemende sieraden dat overblijft.'],
    ['The offer applies while active and while eligible products are available. The seller reserves the right to modify or end the promotion in accordance with applicable law.', 'De aanbieding geldt zolang deze actief is en zolang deelnemende producten beschikbaar zijn. De verkoper behoudt zich het recht voor de actie te wijzigen of te beëindigen binnen de toepasselijke wetgeving.'],
    ['SHOP WOMEN’S JEWELRY', 'BEKIJK DAMESSIERADEN'],
    ['Buy a pendant and save 30% on a chain in the same metal.', 'Koop een hanger en ontvang 30% korting op een ketting van hetzelfde metaal.'],
    ['30% OFF A MATCHING CHAIN', '30% KORTING OP EEN PASSENDE KETTING'],
    ['Promotion Period: August 18, 2026 – September 17, 2026', 'Actieperiode: 18 augustus 2026 – 17 september 2026'],
    ['Promotion Period', 'Actieperiode'],
    ['August 18, 2026 – September 17, 2026', '18 augustus 2026 – 17 september 2026'],
    [': August 18, 2026 – September 17, 2026', ': 18 augustus 2026 – 17 september 2026'],
    ['– Add a pendant and a chain in the same metal to the same order.', '– Voeg een hanger en een ketting van hetzelfde metaal toe aan dezelfde bestelling.'],
    ['– Silver pendant → 30% off one silver chain.', '– Zilveren hanger → 30% korting op één zilveren ketting.'],
    ['– Gold pendant → 30% off one gold chain.', '– Gouden hanger → 30% korting op één gouden ketting.'],
    ['– Metals cannot be mixed: a silver pendant does not qualify a gold chain, and vice versa.', '– Metalen kunnen niet worden gecombineerd: een zilveren hanger geeft geen korting op een gouden ketting en omgekeerd.'],
    ['– One matching chain is discounted for every qualifying pendant. The offer repeats for every complete pendant + chain set in the same order: 2 pendants + 2 matching chains receive 30% off both eligible chains.', '– Voor elke deelnemende hanger krijgt één passende ketting korting. De aanbieding geldt voor elke complete combinatie in dezelfde bestelling: bij 2 hangers en 2 passende kettingen krijgen beide kettingen 30% korting.'],
    ['The discount applies automatically at checkout; no code is required. The 30% discount applies to the qualifying chain, not to the pendant.', 'De korting wordt automatisch toegepast bij het afrekenen; er is geen code nodig. De korting van 30% geldt voor de deelnemende ketting, niet voor de hanger.'],
    ['Return Policy:', 'Retourvoorwaarden:'],
    ['Return Policy', 'Retourvoorwaarden'],
    ['If a qualifying pendant or discounted chain is returned, the refund may be recalculated if the remaining order no longer meets the promotion terms.', 'Als een deelnemende hanger of afgeprijsde ketting wordt geretourneerd, kan de terugbetaling opnieuw worden berekend wanneer de resterende bestelling niet meer aan de actievoorwaarden voldoet.'],
    ['The offer is valid while qualifying products are available. The seller reserves the right to modify or end the promotion in accordance with applicable law.', 'De aanbieding is geldig zolang deelnemende producten beschikbaar zijn. De verkoper behoudt zich het recht voor de actie te wijzigen of te beëindigen binnen de toepasselijke wetgeving.'],
    ['SHOP PENDANTS', 'BEKIJK HANGERS'],
    ["Add any number of men's rings and get 30% off every second ring.", 'Voeg herenringen toe en ontvang 30% korting op elke tweede ring.'],
    ['30% OFF EVERY SECOND RING', '30% KORTING OP ELKE TWEEDE RING'],
    ["– Add at least two men's rings from the Rings collection to the same order.", '– Voeg minimaal twee herenringen uit de collectie Ringen toe aan dezelfde bestelling.'],
    ['– For every two eligible rings, one ring receives a 30% discount. Shopify automatically applies the discount to the lower-priced eligible rings.', '– Voor elke twee deelnemende ringen krijgt één ring 30% korting. Shopify past de korting automatisch toe op de voordeligste deelnemende ring.'],
    ['– 2 rings = 1 discounted ring; 4 rings = 2 discounted rings; 6 rings = 3 discounted rings, and so on.', '– 2 ringen = 1 ring met korting; 4 ringen = 2 ringen met korting; 6 ringen = 3 ringen met korting, enzovoort.'],
    ['– If the number of rings is odd, one ring remains unpaired and does not receive the discount.', '– Bij een oneven aantal blijft één ring ongepaard en ontvangt deze geen korting.'],
    ["– Products outside the men's Rings collection do not qualify.", '– Producten buiten de collectie Herenringen komen niet in aanmerking.'],
    ['– This offer can be used together with the matching-chain promotion when both conditions are met; the discounts apply to different eligible products.', '– Deze aanbieding kan samen met de actie voor een passende ketting worden gebruikt wanneer aan beide voorwaarden is voldaan; de kortingen gelden voor verschillende deelnemende producten.'],
    ['If a qualifying ring is returned, the refund may be recalculated if the remaining order no longer meets the promotion terms.', 'Als een deelnemende ring wordt geretourneerd, kan de terugbetaling opnieuw worden berekend wanneer de resterende bestelling niet meer aan de actievoorwaarden voldoet.'],
    ['SHOP RINGS', 'BEKIJK RINGEN'],
    ["Add men's earrings to one order and save 50% on every second earring.", 'Voeg herenoorbellen toe aan één bestelling en ontvang 50% korting op elke tweede oorbel.'],
    ['50% OFF EVERY SECOND EARRING', '50% KORTING OP ELKE TWEEDE OORBEL'],
    ["– Add two or more men's earrings from the Earrings collection to the same order.", '– Voeg twee of meer herenoorbellen uit de collectie Oorbellen toe aan dezelfde bestelling.'],
    ['– For every complete pair, one earring receives a 50% discount.', '– Voor elk compleet paar krijgt één oorbel 50% korting.'],
    ['– The discount is applied to the lower-priced eligible earring in each pair.', '– De korting wordt toegepast op de voordeligste deelnemende oorbel van elk paar.'],
    ['– Two earrings give one discounted earring; four give two. An unpaired earring remains at full price.', '– Bij twee oorbellen krijgt één oorbel korting; bij vier krijgen twee korting. Een ongepaarde oorbel blijft tegen de volledige prijs.'],
    ["– Products outside the men's Earrings collection do not qualify.", '– Producten buiten de collectie Herenoorbellen komen niet in aanmerking.'],
    ['– This offer can be used together with the matching-chain and second-ring promotions when their conditions are also met; discounts apply to different eligible products.', '– Deze aanbieding kan samen met de acties voor een passende ketting en elke tweede ring worden gebruikt wanneer ook aan die voorwaarden is voldaan; de kortingen gelden voor verschillende deelnemende producten.'],
    ['If a qualifying earring is returned, the refund may be recalculated if the remaining order no longer meets the promotion terms.', 'Als een deelnemende oorbel wordt geretourneerd, kan de terugbetaling opnieuw worden berekend wanneer de resterende bestelling niet meer aan de actievoorwaarden voldoet.'],
    ['SHOP EARRINGS', 'BEKIJK OORBELLEN'],
  ]);
  const localize = (root = document) => {
    const resolveTranslation = (original, allowGeneric = true) => {
      let translated = replacements.get(original);
      if (!translated) {
        const save = original.match(/^Save (\d+)%$/);
        if (save) translated = 'Bespaar ' + save[1] + '%';
      }
      if (!translated) {
        const installments = original.match(/^(\d+) interest-free payments of (.+) with$/);
        if (installments) translated = installments[1] + ' rentevrije betalingen van ' + installments[2] + ' met';
      }
      if (!translated) {
        const reviewCount = original.match(/^(\d+) reviews$/i);
        if (reviewCount) translated = reviewCount[1] + ' beoordelingen';
      }
      if (!translated) {
        const productReviews = original.match(/^Product reviews \((\d+)\)$/i);
        if (productReviews) translated = 'Productbeoordelingen (' + productReviews[1] + ')';
      }
      if (!translated) {
        const storeReviews = original.match(/^Store reviews \((\d+)\)$/i);
        if (storeReviews) translated = 'Winkelbeoordelingen (' + storeReviews[1] + ')';
      }
      if (!translated) {
        const basedOnReviews = original.match(/^Based on (\d+) reviews$/i);
        if (basedOnReviews) translated = 'Gebaseerd op ' + basedOnReviews[1] + ' beoordelingen';
      }
      if (!translated) {
        const rating = original.match(/^([0-9]+(?:[.,][0-9]+)?) out of 5$/i);
        if (rating) translated = rating[1] + ' van 5';
      }
      if (!translated) {
        const page = original.match(/^Page (\d+)$/i);
        if (page) translated = 'Pagina ' + page[1];
      }
      if (!translated) {
        const searchResults = original.match(/^SEARCH RESULTS\s*-\s*(.+)$/i);
        if (searchResults) translated = 'ZOEKRESULTATEN - ' + searchResults[1];
      }
      if (!translated) {
        const backInStock = original.match(/^Back in stock in (.+) days$/i);
        if (backInStock) translated = 'Weer op voorraad binnen ' + backInStock[1] + ' dagen';
      }
      if (!translated && allowGeneric) {
        const cleaned = original
          .replace(/Terms of Service/g, 'Servicevoorwaarden')
          .replace(/\bTerms\b/g, 'Voorwaarden')
          .replace(/we, us en our/g, 'wij, ons en onze')
          .replace(/as is/g, 'zoals ze zijn')
          .replace(/as available/g, 'zoals beschikbaar')
          .replace(/\bSECTION\b/g, 'DEEL')
          .replace(/case-by-case/g, 'per geval')
          .replace(/Silver pendant/g, 'Zilveren hanger')
          .replace(/\(on cord\)/g, '(aan koord)')
          .replace(/runic compass/gi, 'runenkompas')
          .replace(/\bChain\b/g, 'ketting')
          .replace(/\bchain\b/g, 'ketting')
          .replace(/\bEarrings\b/g, 'Oorbellen')
          .replace(/\bEarring\b/g, 'Oorbel')
          .replace(/\bNecklaces\b/g, 'Kettingen')
          .replace(/\bNecklace\b/g, 'Ketting')
          .replace(/\bPendants\b/g, 'Hangers')
          .replace(/\bPendant\b/g, 'Hanger')
          .replace(/\bBracelets\b/g, 'Armbanden')
          .replace(/\bBracelet\b/g, 'Armband')
          .replace(/ear cuffs/gi, 'oormanchetten')
          .replace(/ear cuff/gi, 'oormanchet')
          .replace(/left ear/gi, 'linkeroor')
          .replace(/right ear/gi, 'rechteroor')
          .replace(/inset of/gi, 'inzet van')
          .replace(/вручну/g, 'met de hand');
        if (cleaned !== original) translated = cleaned;
      }
      return translated;
    };
    const translateReviewProductTitle = (original) => {
      const translated = original
        .replace(/Silk cord with silver inserts/gi, 'Zijden koord met zilveren elementen')
        .replace(/\bSilver\b/g, 'Zilveren')
        .replace(/\bsilver\b/g, 'zilveren')
        .replace(/\bsingle\b/gi, 'enkele')
        .replace(/\bearrings\b/gi, 'oorbellen')
        .replace(/\bEarring\b/g, 'Oorbel')
        .replace(/\bweave\b/gi, 'schakelpatroon')
        .replace(/\bcross\b/gi, 'kruis')
        .replace(/\bEngraving\b/g, 'Gravure')
        .replace(/\bbracelet\b/gi, 'armband')
        .replace(/\bpendant\b/gi, 'hanger')
        .replace(/\bon a ketting\b/gi, 'aan een ketting');
      return translated === original ? null : translated;
    };
    const applyText = (node) => {
      const original = node.nodeValue.replace(/\s+/g, ' ').trim();
      const isCustomerReview = Boolean(node.parentElement?.closest('.jdgm-rev'));
      const isReviewProductTitle = Boolean(node.parentElement?.closest('.jm-product-variant-card__product-link'));
      const translated = isReviewProductTitle
        ? (translateReviewProductTitle(original) || resolveTranslation(original, false))
        : resolveTranslation(original, !isCustomerReview);
      if (translated) node.nodeValue = translated;
    };
    const applyHref = (element) => {
      if (!(element instanceof HTMLAnchorElement)) return;
      const href = element.getAttribute('href') || '';
      if (/^\/(?:products|collections|pages|blogs|search|cart|policies)(?:\/|$)/.test(href)) {
        element.setAttribute('href', '/nl-nl' + href);
      }
    };
    if (root.nodeType === Node.TEXT_NODE) applyText(root);
    else {
      if (root.nodeType === Node.ELEMENT_NODE) applyHref(root);
      root.querySelectorAll?.('a[href]').forEach(applyHref);
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) applyText(node);
    }
  };
  const start = () => {
    localize(document);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
/* REYTEL_NL_VISIBLE_COPY_V2_END */

// REYTEL Danish storefront UI localization for third-party widgets and generated accessibility labels.
(function () {
	if ((document.documentElement.lang || '').toLowerCase().split('-')[0] !== 'da') return;
	const exactText = new Map([
		['JEWELRY THAT SPEAKS FOR YOU', 'SMYKKER, DER TALER FOR DIG'],
		['Unlock your exclusive 5% welcome offer', 'Få dit eksklusive velkomsttilbud på 5 %'],
		['UNLOCK 5% OFF', 'FÅ 5 % RABAT'], ['UNLOCK OFFER', 'AKTIVÉR TILBUDDET'],
		['No, thanks', 'Nej tak'], ['Submit', 'Send'], ['Close teaser', 'Luk tilbuddet'],
		['Close dialog', 'Luk dialogboksen'], ['Write a review', 'Skriv en anmeldelse'],
		['Sort by', 'Sortér efter'], ['FULLSCREEN Form', 'Fuldskærmsformular'],
		['Sale', 'Udsalg'], ['Chat', 'Chat'], ['Previous', 'Forrige'], ['Next', 'Næste'],
	]);
	const ariaText = new Map([
		['Previous slide', 'Forrige slide'], ['Next slide', 'Næste slide'],
		['Previous page', 'Forrige side'], ['Next page', 'Næste side'],
		['Close teaser', 'Luk tilbuddet'], ['Close dialog', 'Luk dialogboksen'],
		['minus', 'Sænk antal'], ['quantity', 'Antal'], ['plus', 'Øg antal'],
	]);
	const altText = new Map([
		['Slider prev', 'Forrige billede'], ['Slider next', 'Næste billede'],
		['Previous slide', 'Forrige billede'], ['Next slide', 'Næste billede'],
		['Verified Checkmark', 'Verificeret anmeldelse'], ['Search icon', 'Søgeikon'],
		['image', 'Billede fra anmeldelse'],
	]);
	function localizeElement(element) {
		if (!(element instanceof Element)) return;
		if (/^(SCRIPT|STYLE|TEXTAREA|NOSCRIPT|TEMPLATE)$/.test(element.tagName)) return;
		const ariaLabel = element.getAttribute('aria-label');
		if (ariaText.has(ariaLabel)) element.setAttribute('aria-label', ariaText.get(ariaLabel));
		if (/^Average rating is ([0-9.]+) stars$/.test(ariaLabel || '')) element.setAttribute('aria-label', ariaLabel.replace(/^Average rating is ([0-9.]+) stars$/, 'Gennemsnitlig bedømmelse: $1 ud af 5 stjerner'));
		if (/^(\d+) star review$/.test(ariaLabel || '')) element.setAttribute('aria-label', ariaLabel.replace(/^(\d+) star review$/, '$1-stjernet anmeldelse'));
		const alt = element.getAttribute('alt');
		if (altText.has(alt)) element.setAttribute('alt', altText.get(alt));
		const renderedText = (element.innerText || '').trim();
		if (((element.id || '').startsWith('rich-text-') || element.tagName === 'BUTTON') && exactText.has(renderedText)) { element.textContent = exactText.get(renderedText); return; }
		if (element.children.length === 0) { const currentText = element.textContent.trim(); if (exactText.has(currentText)) element.textContent = exactText.get(currentText); }
	}
	function localizeTree(root) {
		if (root instanceof Element) localizeElement(root);
		if (!root.querySelectorAll) return;
		root.querySelectorAll('[aria-label], [alt], [id^="rich-text-"], .jdgm-write-rev-link, .jdgm-sort-dropdown, .jdgm-sort-dropdown-label, .jdgm-sort-dropdown option, .needsclick, title, strong, button').forEach(localizeElement);
	}
	function startLocalization() {
		localizeTree(document);
	}
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startLocalization, {once: true});
	else startLocalization();
})();

/* REYTEL_DA_VISIBLE_COPY_V6_START */
(() => {
  if (!(document.documentElement.lang || '').toLowerCase().startsWith('da')) return;
  const imageAltCopy = new Map([
    ['Chain Bracelets', 'Kædearmbånd'],
    ["Men's Pendant Necklaces", 'Vedhængshalskæder til mænd'],
    ["Men's Animal Pendants", 'Dyrevedhæng til mænd'],
    ["Men's Zodiac Jewelry", 'Stjernetegnssmykker til mænd'],
    ["Men's Christian Pendants & Necklaces", 'Kristne vedhæng og halskæder til mænd'],
    ["Men's Nordic Pendants & Necklaces", 'Nordiske vedhæng og halskæder til mænd'],
    ["Men's Animal Rings", 'Dyreringe til mænd'],
    ["Men's Geometric Rings", 'Geometriske ringe til mænd'],
    ["Men's Christian Rings", 'Kristne ringe til mænd'],
    ["Men's Gemstone Bracelets", 'Armbånd med ædelsten til mænd'],
    ["Men's Chains", 'Kæder til mænd'],
    ["Men's Ear Cuffs", 'Ear cuffs til mænd'],
    ["Men's Gemstone Earrings", 'Øreringe med ædelsten til mænd'],
    ["Men's Animal Jewelry", 'Dyresmykker til mænd'],
  ]);
  function localizeImageAlts(root) {
    const images = [];
    if (root?.nodeType === Node.ELEMENT_NODE && root.matches?.('img')) images.push(root);
    if (root?.querySelectorAll) images.push(...root.querySelectorAll('img'));
    for (const image of images) {
      const translated = imageAltCopy.get(image.alt);
      if (translated) image.alt = translated;
    }
  }
  const copy = new Map([
    ['EARRINGS', 'ØRERINGE'], ['Earrings', 'Øreringe'],
    ['NECKLACES', 'HALSKÆDER'], ['Necklaces', 'Halskæder'],
    ['RINGS', 'RINGE'], ['Rings', 'Ringe'],
    ['BRACELETS', 'ARMBÅND'], ['Bracelets', 'Armbånd'],
    ['CUFFS', 'EAR CUFFS'], ['Cuffs', 'Ear cuffs'],
    ['ADD ENGRAVING', 'TILFØJ GRAVERING'], ['Add engraving', 'Tilføj gravering'],
    ['ADD TO BAG', 'LÆG I KURV'], ['Add to bag', 'Læg i kurv'],
    ['Handcrafted in Europe', 'Håndlavet i Europa'],
    ['2-Year Warranty', '2 års garanti'], ['14-Day Return Policy', '14 dages returret'],
    ['CARE', 'PLEJE'], ['WARRANTY', 'GARANTI'],
    ['SHIPPING & RETURNS', 'LEVERING OG RETURNERING'],
    ['BUY THIS PENDANT AND GET 30% OFF ANY CHAIN', 'KØB DETTE VEDHÆNG, OG FÅ 30 % RABAT PÅ EN VALGFRI KÆDE'],
    ['PACKAGING THAT TELLS A STORY', 'EMBALLAGE MED EN HISTORIE'],
    ['Packaging That Tells a Story', 'Emballage med en historie'],
    ['CUSTOMER REVIEWS', 'KUNDEANMELDELSER'], ['Customer Reviews', 'Kundeanmeldelser'],
    ['Be the first to write a review', 'Vær den første til at skrive en anmeldelse'],
    ['Write a review', 'Skriv en anmeldelse'],
    ['JUST SEEN', 'SENEST SET'], ['Just seen', 'Senest set'],
    ['SHOP WOMEN’S JEWELRY', 'SE DAMESMYKKER'], ['SHOP PENDANTS', 'SE VEDHÆNG'],
    ['SHOP RINGS', 'SE RINGE'], ['SHOP EARRINGS', 'SE ØRERINGE'],
    ['MATERIAL:', 'MATERIALE:'], ['SELECT SIZE:', 'VÆLG STØRRELSE:'],
    ['SIZE GUIDE', 'STØRRELSESGUIDE'], ['DETAILS', 'DETALJER'],
    ['Material:', 'Materiale:'], ['Select size:', 'Vælg størrelse:'],
    ['Size guide', 'Størrelsesguide'], ['Details', 'Detaljer'],
    ['14k White gold', '14K hvidguld'], ['14k Yellow gold', '14K gult guld'],
    ['14k Rose gold', '14K rosaguld'], ['925 Sterling Silver', '925 sterlingsølv'],
    ['GET 30% OFF EVERY SECOND RING IN YOUR ORDER', 'FÅ 30 % RABAT PÅ HVER ANDEN RING I DIN ORDRE'],
    ['Get 30% off every second ring in your order', 'Få 30 % rabat på hver anden ring i din ordre'],
    ['Most Recent', 'Nyeste'], ['Highest Rating', 'Højeste bedømmelse'],
    ['Lowest Rating', 'Laveste bedømmelse'], ['Only Pictures', 'Kun billeder'],
    ['Pictures First', 'Billeder først'], ['Videos First', 'Videoer først'],
    ['Most Helpful', 'Mest nyttige'], ['REVIEWS IN OTHER LANGUAGES', 'ANMELDELSER PÅ ANDRE SPROG'],
    ['Review written in Shop App', 'Anmeldelse skrevet i Shop-appen'],
    ['Reviews in Other Languages', 'Anmeldelser på andre sprog'],
  ]);
  function localize(root) {
    if (!root) return;
    localizeImageAlts(root);
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    for (const node of nodes) {
      const raw = node.nodeValue || '';
      const trimmed = raw.trim();
      let translated = copy.get(trimmed);
      if (!translated) {
        const save = trimmed.match(/^Save (\d+)%$/); if (save) translated = 'Spar ' + save[1] + ' %';
        const reviews = trimmed.match(/^(\d+) reviews?$/i); if (reviews) translated = reviews[1] + (reviews[1] === '1' ? ' anmeldelse' : ' anmeldelser');
        const based = trimmed.match(/^Based on (\d+) reviews?$/i); if (based) translated = 'Baseret på ' + based[1] + (based[1] === '1' ? ' anmeldelse' : ' anmeldelser');
        const rating = trimmed.match(/^([0-9.,]+) out of 5$/i); if (rating) translated = rating[1] + ' ud af 5';
        const installments = trimmed.match(/^(\d+) interest-free payments of (.+) with$/i); if (installments) translated = installments[1] + ' rentefri betalinger på ' + installments[2] + ' med';
      }
      if (translated) node.nodeValue = raw.replace(trimmed, translated);
    }
  }
  const start = () => {
    localize(document.body);
    new MutationObserver(ms => ms.forEach(m => m.addedNodes.forEach(n => {
      if (n.nodeType === Node.TEXT_NODE) {
        const raw = n.nodeValue || ''; const trimmed = raw.trim(); let translated = copy.get(trimmed);
        if (!translated) { const save=trimmed.match(/^Save (\d+)%$/); if(save) translated='Spar '+save[1]+' %'; const reviews=trimmed.match(/^(\d+) reviews?$/i); if(reviews) translated=reviews[1]+(reviews[1]==='1'?' anmeldelse':' anmeldelser'); const based=trimmed.match(/^Based on (\d+) reviews?$/i); if(based) translated='Baseret på '+based[1]+(based[1]==='1'?' anmeldelse':' anmeldelser'); const rating=trimmed.match(/^([0-9.,]+) out of 5$/i); if(rating) translated=rating[1]+' ud af 5'; const installments=trimmed.match(/^(\d+) interest-free payments of (.+) with$/i); if(installments) translated=installments[1]+' rentefri betalinger på '+installments[2]+' med'; }
        if (translated) n.nodeValue = raw.replace(trimmed, translated);
      } else if (n.nodeType === Node.ELEMENT_NODE) localize(n);
    }))).observe(document.body, {childList: true, subtree: true});
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true}); else start();
})();
/* REYTEL_DA_VISIBLE_COPY_V6_END */

/* REYTEL_ES_STOREFRONT_LOCALIZATION_V1_START */
(() => {
  if (!(document.documentElement.lang || '').toLowerCase().startsWith('es')) return;
  const exact = new Map([
    ['JEWELRY THAT SPEAKS FOR YOU', 'JOYAS QUE HABLAN DE TI'],
    ['Unlock your exclusive 5% welcome offer', 'Consigue tu 5 % de descuento de bienvenida'],
    ['UNLOCK 5% OFF', 'CONSIGUE UN 5 % DE DESCUENTO'], ['UNLOCK OFFER', 'ACTIVAR OFERTA'],
    ['No, thanks', 'No, gracias'], ['Submit', 'Enviar'], ['Close teaser', 'Cerrar oferta'],
    ['Close dialog', 'Cerrar ventana'], ['Write a review', 'Escribir una reseña'],
    ['See all reviews', 'Ver todas las reseñas'], ['Customer Reviews', 'Reseñas de clientes'],
    ['CUSTOMER REVIEWS', 'RESEÑAS DE CLIENTES'], ['Reviews in Other Languages', 'Reseñas en otros idiomas'],
    ['Most Recent', 'Más recientes'], ['Highest Rating', 'Mejor valoración'], ['Lowest Rating', 'Peor valoración'],
    ['Only Pictures', 'Solo fotos'], ['Pictures First', 'Fotos primero'], ['Videos First', 'Vídeos primero'],
    ['Most Helpful', 'Más útiles'], ['Verified', 'Verificada'], ['United States', 'Estados Unidos'],
    ['Read more', 'Leer más'], ['Sort by', 'Ordenar por'], ['Sale', 'Rebajas'], ['Chat', 'Chat'],
    ['Previous', 'Anterior'], ['Next', 'Siguiente'], ['Packaging That Tells a Story', 'Un embalaje que cuenta una historia'],
    ['PACKAGING THAT TELLS A STORY', 'UN EMBALAJE QUE CUENTA UNA HISTORIA'],
    ['EARRINGS', 'PENDIENTES'], ['Earrings', 'Pendientes'], ['NECKLACES', 'COLLARES'], ['Necklaces', 'Collares'],
    ['RINGS', 'ANILLOS'], ['Rings', 'Anillos'], ['BRACELETS', 'PULSERAS'], ['Bracelets', 'Pulseras'],
    ['CUFFS', 'EARCUFFS'], ['Cuffs', 'Earcuffs'], ['ADD ENGRAVING', 'AÑADIR GRABADO'],
    ['Add engraving', 'Añadir grabado'], ['ADD TO BAG', 'AÑADIR A LA CESTA'], ['Add to bag', 'Añadir a la cesta'],
    ['Handcrafted in Europe', 'Hecho a mano en Europa'], ['2-Year Warranty', '2 años de garantía'],
    ['14-Day Return Policy', 'Devoluciones en 14 días'], ['CARE', 'CUIDADOS'], ['WARRANTY', 'GARANTÍA'],
    ['SHIPPING & RETURNS', 'ENVÍOS Y DEVOLUCIONES'], ['DETAILS', 'DETALLES'], ['SIZE GUIDE', 'GUÍA DE TALLAS'],
    ['MATERIAL:', 'MATERIAL:'], ['SELECT SIZE:', 'ELIGE LA TALLA:'], ['Be the first to write a review', 'Sé el primero en escribir una reseña'],
    ['JUST SEEN', 'VISTO RECIENTEMENTE'], ['Just seen', 'Visto recientemente'],
    ['SHOP WOMEN’S JEWELRY', 'VER JOYAS PARA MUJER'], ['SHOP PENDANTS', 'VER COLGANTES'],
    ['SHOP RINGS', 'VER ANILLOS'], ['SHOP EARRINGS', 'VER PENDIENTES'],
    ['BUY THIS PENDANT AND GET 30% OFF ANY CHAIN', 'COMPRA ESTE COLGANTE Y CONSIGUE UN 30 % DE DESCUENTO EN CUALQUIER CADENA'],
    ['Review collected via store invitation', 'Reseña recopilada mediante una invitación de la tienda'],
    ['Review earned a reward for future purchase', 'Esta reseña obtuvo una recompensa para una futura compra'],
    ['Review collected from a store visitor', 'Reseña recopilada de un visitante de la tienda'],
  ]);
  const aria = new Map([
    ['Previous slide', 'Diapositiva anterior'], ['Next slide', 'Diapositiva siguiente'],
    ['Previous page', 'Página anterior'], ['Next page', 'Página siguiente'],
    ['Close teaser', 'Cerrar oferta'], ['Close dialog', 'Cerrar ventana'],
    ['minus', 'Reducir cantidad'], ['quantity', 'Cantidad'], ['plus', 'Aumentar cantidad'],
  ]);
  const alts = new Map([
    ['Slider prev', 'Imagen anterior'], ['Slider next', 'Imagen siguiente'],
    ['Previous slide', 'Imagen anterior'], ['Next slide', 'Imagen siguiente'],
    ['Verified Checkmark', 'Reseña verificada'], ['Search icon', 'Icono de búsqueda'],
    ['image', 'Imagen de la reseña'], ['User picture', 'Foto del cliente'],
    ["Men's Animal Pendants", 'Colgantes de animales para hombre'],
    ["Men's Zodiac Jewelry", 'Joyas del zodiaco para hombre'],
    ["Men's Christian Pendants & Necklaces", 'Colgantes y collares cristianos para hombre'],
    ["Men's Nordic Pendants & Necklaces", 'Colgantes y collares nórdicos para hombre'],
    ["Men's Animal Rings", 'Anillos de animales para hombre'],
    ["Men's Geometric Rings", 'Anillos geométricos para hombre'],
    ["Men's Christian Rings", 'Anillos cristianos para hombre'],
    ['Chain Bracelets', 'Pulseras de cadena'],
    ['Bangle Bracelets', 'Brazaletes rígidos'],
    ["Men's Gemstone Bracelets", 'Pulseras con piedras para hombre'],
    ["Men's Chains", 'Cadenas para hombre'],
    ["Men's Pendant Necklaces", 'Collares con colgante para hombre'],
    ['Stud earrings', 'Pendientes de botón'], ['Hoop earrings', 'Pendientes de aro'],
    ["Men's Ear Cuffs", 'Earcuffs para hombre'],
    ["Men's Gemstone Earrings", 'Pendientes con piedras para hombre'],
    ["Men's Animal Jewelry", 'Joyas de animales para hombre'],
  ]);
  const localizeElement = (element) => {
    if (!(element instanceof Element)) return;
    if (/^(SCRIPT|STYLE|TEXTAREA|NOSCRIPT|TEMPLATE)$/.test(element.tagName)) return;
    const ariaLabel = element.getAttribute('aria-label') || '';
    if (aria.has(ariaLabel)) element.setAttribute('aria-label', aria.get(ariaLabel));
    let match = ariaLabel.match(/^Average rating is ([0-9.]+) stars$/);
    if (match) element.setAttribute('aria-label', 'Valoración media: ' + match[1].replace('.', ',') + ' de 5 estrellas');
    match = ariaLabel.match(/^(\d+) star review$/);
    if (match) element.setAttribute('aria-label', 'Reseña de ' + match[1] + ' estrellas');
    const alt = element.getAttribute('alt');
    if (alts.has(alt)) element.setAttribute('alt', alts.get(alt));
    if (element instanceof HTMLAnchorElement) {
      const href = element.getAttribute('href') || '';
      if (/^\/(?:products|collections|pages|blogs|search|cart|policies)(?:\/|$)/.test(href)) element.setAttribute('href', '/es-es' + href);
    }
  };
  const localizeTextNode = (node) => {
    if (!(node instanceof Text) || node.parentElement?.closest('script, style, textarea, .jdgm-rev__body')) return;
    const raw = node.nodeValue || '';
    const value = raw.replace(/\s+/g, ' ').trim();
    let replacement = exact.get(value);
    let match = value.match(/^(\d+) reviews?$/i);
    if (match) replacement = match[1] + ' ' + (Number(match[1]) === 1 ? 'reseña' : 'reseñas');
    match = value.match(/^Based on (\d+) reviews?$/i);
    if (match) replacement = 'Basado en ' + match[1] + ' ' + (Number(match[1]) === 1 ? 'reseña' : 'reseñas');
    match = value.match(/^([0-9]+(?:\.[0-9]+)?) out of 5$/i);
    if (match) replacement = match[1].replace('.', ',') + ' de 5';
    match = value.match(/^SEARCH RESULTS\s*[-–]\s*(.+)$/i);
    if (match) replacement = 'RESULTADOS DE BÚSQUEDA — ' + match[1];
    if (replacement && replacement !== value) node.nodeValue = raw.replace(value, replacement);
  };
  const scan = (root) => {
    if (root instanceof Text) return localizeTextNode(root);
    if (root instanceof Element) localizeElement(root);
    root.querySelectorAll?.('[aria-label], [alt], a[href]').forEach(localizeElement);
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) localizeTextNode(node);
  };
  const start = () => {
    scan(document);
    new MutationObserver((mutations) => mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') localizeElement(mutation.target);
      if (mutation.type === 'characterData') localizeTextNode(mutation.target);
      mutation.addedNodes.forEach(scan);
    })).observe(document.documentElement, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
/* REYTEL_ES_STOREFRONT_LOCALIZATION_V1_END */

/* REYTEL_EL_STOREFRONT_LOCALIZATION_V1_START */
(() => {
  if (!(document.documentElement.lang || '').toLowerCase().startsWith('el')) return;
  const copy = new Map([
    ['JEWELRY THAT SPEAKS FOR YOU', 'ΚΟΣΜΗΜΑΤΑ ΠΟΥ ΜΙΛΟΥΝ ΓΙΑ ΕΣΑΣ'], ['Unlock your exclusive 5% welcome offer', 'Ενεργοποιήστε την αποκλειστική έκπτωση καλωσορίσματος 5%'],
    ['UNLOCK 5% OFF', 'ΚΕΡΔΙΣΤΕ 5% ΕΚΠΤΩΣΗ'], ['UNLOCK OFFER', 'ΕΝΕΡΓΟΠΟΙΗΣΗ ΠΡΟΣΦΟΡΑΣ'], ['No, thanks', 'Όχι, ευχαριστώ'], ['Submit', 'Υποβολή'],
    ['Close teaser', 'Κλείσιμο προσφοράς'], ['Close dialog', 'Κλείσιμο παραθύρου'], ['Write a review', 'Γράψτε μια αξιολόγηση'],
    ['See all reviews', 'Δείτε όλες τις αξιολογήσεις'], ['Customer Reviews', 'Αξιολογήσεις πελατών'], ['CUSTOMER REVIEWS', 'ΑΞΙΟΛΟΓΗΣΕΙΣ ΠΕΛΑΤΩΝ'],
    ['Reviews in Other Languages', 'Αξιολογήσεις σε άλλες γλώσσες'], ['Most Recent', 'Πιο πρόσφατες'], ['Highest Rating', 'Υψηλότερη βαθμολογία'],
    ['Lowest Rating', 'Χαμηλότερη βαθμολογία'], ['Only Pictures', 'Μόνο φωτογραφίες'], ['Pictures First', 'Πρώτα οι φωτογραφίες'],
    ['Videos First', 'Πρώτα τα βίντεο'], ['Most Helpful', 'Πιο χρήσιμες'], ['Verified', 'Επαληθευμένη'], ['Read more', 'Διαβάστε περισσότερα'],
    ['Sort by', 'Ταξινόμηση κατά'], ['Sale', 'Εκπτώσεις'], ['Previous', 'Προηγούμενο'], ['Next', 'Επόμενο'],
    ['Packaging That Tells a Story', 'ΣΥΣΚΕΥΑΣΙΑ ΜΕ ΙΣΤΟΡΙΑ'], ['PACKAGING THAT TELLS A STORY', 'ΣΥΣΚΕΥΑΣΙΑ ΜΕ ΙΣΤΟΡΙΑ'],
    ['EARRINGS', 'ΣΚΟΥΛΑΡΙΚΙΑ'], ['Earrings', 'Σκουλαρίκια'], ['NECKLACES', 'ΚΟΛΙΕ'], ['Necklaces', 'Κολιέ'], ['RINGS', 'ΔΑΧΤΥΛΙΔΙΑ'], ['Rings', 'Δαχτυλίδια'],
    ['BRACELETS', 'ΒΡΑΧΙΟΛΙΑ'], ['Bracelets', 'Βραχιόλια'], ['CUFFS', 'EAR CUFFS'], ['ADD ENGRAVING', 'ΠΡΟΣΘΗΚΗ ΧΑΡΑΞΗΣ'],
    ['Add engraving', 'Προσθήκη χάραξης'], ['ADD TO BAG', 'ΠΡΟΣΘΗΚΗ ΣΤΟ ΚΑΛΑΘΙ'], ['Add to bag', 'Προσθήκη στο καλάθι'],
    ['Handcrafted in Europe', 'Χειροποίητο στην Ευρώπη'], ['2-Year Warranty', 'Εγγύηση 2 ετών'], ['14-Day Return Policy', 'Επιστροφές εντός 14 ημερών'],
    ['CARE', 'ΦΡΟΝΤΙΔΑ'], ['WARRANTY', 'ΕΓΓΥΗΣΗ'], ['SHIPPING & RETURNS', 'ΑΠΟΣΤΟΛΗ ΚΑΙ ΕΠΙΣΤΡΟΦΕΣ'], ['DETAILS', 'ΛΕΠΤΟΜΕΡΕΙΕΣ'],
    ['SIZE GUIDE', 'ΟΔΗΓΟΣ ΜΕΓΕΘΩΝ'], ['MATERIAL:', 'ΥΛΙΚΟ:'], ['SELECT SIZE:', 'ΕΠΙΛΕΞΤΕ ΜΕΓΕΘΟΣ:'],
    ['Be the first to write a review', 'Γίνετε ο πρώτος που θα γράψει αξιολόγηση'], ['JUST SEEN', 'ΠΡΟΣΦΑΤΑ ΠΡΟΒΛΗΘΗΚΑΝ'], ['Just seen', 'Πρόσφατα προβλήθηκαν'],
    ['SHOP WOMEN’S JEWELRY', 'ΓΥΝΑΙΚΕΙΑ ΚΟΣΜΗΜΑΤΑ'], ['SHOP PENDANTS', 'ΔΕΙΤΕ ΜΕΝΤΑΓΙΟΝ'], ['SHOP RINGS', 'ΔΕΙΤΕ ΔΑΧΤΥΛΙΔΙΑ'],
    ['SHOP EARRINGS', 'ΔΕΙΤΕ ΣΚΟΥΛΑΡΙΚΙΑ'], ['BUY THIS PENDANT AND GET 30% OFF ANY CHAIN', 'ΑΓΟΡΑΣΤΕ ΑΥΤΟ ΤΟ ΜΕΝΤΑΓΙΟΝ ΚΑΙ ΚΕΡΔΙΣΤΕ 30% ΕΚΠΤΩΣΗ ΣΕ ΟΠΟΙΑΔΗΠΟΤΕ ΑΛΥΣΙΔΑ'],
  ]);
  const aria = new Map([['Previous slide', 'Προηγούμενη διαφάνεια'], ['Next slide', 'Επόμενη διαφάνεια'], ['Previous page', 'Προηγούμενη σελίδα'],
    ['Next page', 'Επόμενη σελίδα'], ['Close teaser', 'Κλείσιμο προσφοράς'], ['Close dialog', 'Κλείσιμο παραθύρου'], ['minus', 'Μείωση ποσότητας'],
    ['quantity', 'Ποσότητα'], ['plus', 'Αύξηση ποσότητας']]);
  const alts = new Map([['Slider prev', 'Προηγούμενη εικόνα'], ['Slider next', 'Επόμενη εικόνα'], ['Previous slide', 'Προηγούμενη εικόνα'],
    ['Next slide', 'Επόμενη εικόνα'], ['Verified Checkmark', 'Επαληθευμένη αξιολόγηση'], ['Search icon', 'Εικονίδιο αναζήτησης'], ['image', 'Εικόνα αξιολόγησης']]);
  const element = (el) => {
    if (!(el instanceof Element)) return;
    const label = el.getAttribute('aria-label') || '';
    if (aria.has(label)) el.setAttribute('aria-label', aria.get(label));
    let match = label.match(/^Average rating is ([0-9.]+) stars$/);
    if (match) el.setAttribute('aria-label', 'Μέση βαθμολογία: ' + match[1].replace('.', ',') + ' από 5 αστέρια');
    const alt = el.getAttribute('alt'); if (alts.has(alt)) el.setAttribute('alt', alts.get(alt));
  };
  const text = (node) => {
    if (!(node instanceof Text) || node.parentElement?.closest('script, style, textarea, .jdgm-rev__body')) return;
    const raw = node.nodeValue || ''; const value = raw.replace(/\s+/g, ' ').trim(); let replacement = copy.get(value); let match;
    if ((match = value.match(/^(\d+) reviews?$/i))) replacement = match[1] + (Number(match[1]) === 1 ? ' αξιολόγηση' : ' αξιολογήσεις');
    if ((match = value.match(/^Based on (\d+) reviews?$/i))) replacement = 'Με βάση ' + match[1] + (Number(match[1]) === 1 ? ' αξιολόγηση' : ' αξιολογήσεις');
    if ((match = value.match(/^([0-9]+(?:\.[0-9]+)?) out of 5$/i))) replacement = match[1].replace('.', ',') + ' από 5';
    if ((match = value.match(/^SEARCH RESULTS\s*[-–]\s*(.+)$/i))) replacement = 'ΑΠΟΤΕΛΕΣΜΑΤΑ ΑΝΑΖΗΤΗΣΗΣ — ' + match[1];
    if (replacement && replacement !== value) node.nodeValue = raw.replace(value, replacement);
  };
  const scan = (root) => {
    if (root instanceof Text) return text(root); if (!(root instanceof Element)) return;
    element(root); root.querySelectorAll?.('[aria-label], [alt]').forEach(element);
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT); let node; while ((node = walker.nextNode())) text(node);
  };
  const start = () => {
    scan(document.body);
    new MutationObserver((mutations) => mutations.forEach((mutation) => mutation.addedNodes.forEach(scan)))
      .observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();
/* REYTEL_EL_STOREFRONT_LOCALIZATION_V1_END */


/* REYTEL_BALTIC_WIDGET_COPY_START */
(() => {
 const lang=(document.documentElement.lang||'').toLowerCase().split('-')[0];
 const dictionaries={"lt":{"Previous slide":"Ankstesnė skaidrė","Next slide":"Kita skaidrė","Previous page":"Ankstesnis puslapis","Next page":"Kitas puslapis","Payment method":"Mokėjimo būdas","Men wearing silver rings by REYTEL":"Vyrai su REYTEL sidabriniais žiedais","Chat":"Pokalbis","Close teaser":"Uždaryti pasiūlymą","Close dialog":"Uždaryti langą","UNLOCK 5% OFF":"GAUKITE 5 % NUOLAIDĄ","UNLOCK OFFER":"GAUKITE PASIŪLYMĄ","Unlock your exclusive 5% welcome offer":"Gaukite išskirtinę 5 % nuolaidą pirmajam užsakymui","No, thanks":"Ne, ačiū","Submit":"Siųsti","Write a review":"Parašyti atsiliepimą","See all reviews":"Peržiūrėti visus atsiliepimus","Customer Reviews":"Klientų atsiliepimai","CUSTOMER REVIEWS":"KLIENTŲ ATSILIEPIMAI","Reviews in Other Languages":"Atsiliepimai kitomis kalbomis","Most Recent":"Naujausi","Highest Rating":"Aukščiausiai įvertinti","Lowest Rating":"Žemiausiai įvertinti","Only Pictures":"Tik su nuotraukomis","Pictures First":"Pirmiausia su nuotraukomis","Videos First":"Pirmiausia su vaizdo įrašais","Most Helpful":"Naudingiausi","Verified":"Patvirtinta","Verified Checkmark":"Patvirtintas atsiliepimas","Read more":"Skaityti daugiau","Be the first to write a review":"Parašykite pirmą atsiliepimą","UNLOCK":"GAUKITE","5% OFF":"5 % NUOLAIDĄ","Unread replies":"Neskaityti atsakymai","Back to chat":"Grįžti į pokalbį","Profile":"Profilis","Sign out":"Atsijungti","Buyer Profile":"Pirkėjo profilis","Create a new chat":"Pradėti naują pokalbį","Expand chat":"Išskleisti pokalbį","Collapse chat":"Sutraukti pokalbį","Close chat":"Uždaryti pokalbį","Start a new chat?":"Pradėti naują pokalbį?","Sign in to save your chat history, or continue without signing in.":"Prisijunkite, kad išsaugotumėte pokalbių istoriją, arba tęskite neprisijungę.","Sign in":"Prisijungti","Continue without signing in":"Tęsti neprisijungus","Loading chat":"Pokalbis įkeliamas","Chat isn’t available right now":"Pokalbis šiuo metu neprieinamas","Something went wrong while loading the chat.":"Įkeliant pokalbį įvyko klaida.","Try again":"Bandyti dar kartą","Add to wishlist":"Pridėti į norų sąrašą","Remove from wishlist":"Pašalinti iš norų sąrašo","Sort by":"Rūšiuoti pagal",">> REYTEL replied:":">> REYTEL atsakė:","United States":"Jungtinės Valstijos"},"lv":{"Previous slide":"Iepriekšējais slaids","Next slide":"Nākamais slaids","Previous page":"Iepriekšējā lapa","Next page":"Nākamā lapa","Payment method":"Maksājuma veids","Men wearing silver rings by REYTEL":"Vīrieši ar REYTEL sudraba gredzeniem","Chat":"Saruna","Close teaser":"Aizvērt piedāvājumu","Close dialog":"Aizvērt logu","UNLOCK 5% OFF":"SAŅEMIET 5 % ATLAIDI","UNLOCK OFFER":"SAŅEMIET PIEDĀVĀJUMU","Unlock your exclusive 5% welcome offer":"Saņemiet īpašu 5 % atlaidi pirmajam pasūtījumam","No, thanks":"Nē, paldies","Submit":"Nosūtīt","Write a review":"Rakstīt atsauksmi","See all reviews":"Skatīt visas atsauksmes","Customer Reviews":"Klientu atsauksmes","CUSTOMER REVIEWS":"KLIENTU ATSAUKSMES","Reviews in Other Languages":"Atsauksmes citās valodās","Most Recent":"Jaunākās","Highest Rating":"Augstākais vērtējums","Lowest Rating":"Zemākais vērtējums","Only Pictures":"Tikai ar fotoattēliem","Pictures First":"Vispirms ar fotoattēliem","Videos First":"Vispirms ar video","Most Helpful":"Noderīgākās","Verified":"Apstiprināta","Verified Checkmark":"Apstiprināta atsauksme","Read more":"Lasīt vairāk","Be the first to write a review":"Uzrakstiet pirmo atsauksmi","UNLOCK":"SAŅEMIET","5% OFF":"5 % ATLAIDI","Unread replies":"Nelasītas atbildes","Back to chat":"Atgriezties sarunā","Profile":"Profils","Sign out":"Izrakstīties","Buyer Profile":"Pircēja profils","Create a new chat":"Sākt jaunu sarunu","Expand chat":"Izvērst sarunu","Collapse chat":"Samazināt sarunu","Close chat":"Aizvērt sarunu","Start a new chat?":"Sākt jaunu sarunu?","Sign in to save your chat history, or continue without signing in.":"Pierakstieties, lai saglabātu sarunu vēsturi, vai turpiniet bez pierakstīšanās.","Sign in":"Pierakstīties","Continue without signing in":"Turpināt bez pierakstīšanās","Loading chat":"Saruna tiek ielādēta","Chat isn’t available right now":"Saruna pašlaik nav pieejama","Something went wrong while loading the chat.":"Ielādējot sarunu, radās kļūda.","Try again":"Mēģināt vēlreiz","Add to wishlist":"Pievienot vēlmju sarakstam","Remove from wishlist":"Noņemt no vēlmju saraksta","Sort by":"Kārtot pēc",">> REYTEL replied:":">> REYTEL atbildēja:","United States":"Amerikas Savienotās Valstis"},"et":{"Previous slide":"Eelmine slaid","Next slide":"Järgmine slaid","Previous page":"Eelmine leht","Next page":"Järgmine leht","Payment method":"Makseviis","Men wearing silver rings by REYTEL":"Mehed REYTELi hõbesõrmustega","Chat":"Vestlus","Close teaser":"Sulge pakkumine","Close dialog":"Sulge aken","UNLOCK 5% OFF":"SAA 5% ALLAHINDLUST","UNLOCK OFFER":"KASUTA PAKKUMIST","Unlock your exclusive 5% welcome offer":"Saa esimesele tellimusele eksklusiivne 5% allahindlus","No, thanks":"Ei, aitäh","Submit":"Saada","Write a review":"Kirjuta arvustus","See all reviews":"Vaata kõiki arvustusi","Customer Reviews":"Klientide arvustused","CUSTOMER REVIEWS":"KLIENTIDE ARVUSTUSED","Reviews in Other Languages":"Arvustused teistes keeltes","Most Recent":"Uusimad","Highest Rating":"Kõrgeim hinnang","Lowest Rating":"Madalaim hinnang","Only Pictures":"Ainult piltidega","Pictures First":"Piltidega eespool","Videos First":"Videotega eespool","Most Helpful":"Kasulikumad","Verified":"Kinnitatud","Verified Checkmark":"Kinnitatud arvustus","Read more":"Loe edasi","Be the first to write a review":"Kirjuta esimene arvustus","UNLOCK":"SAA","5% OFF":"5% ALLAHINDLUST","Unread replies":"Lugemata vastused","Back to chat":"Tagasi vestlusse","Profile":"Profiil","Sign out":"Logi välja","Buyer Profile":"Ostja profiil","Create a new chat":"Alusta uut vestlust","Expand chat":"Laienda vestlust","Collapse chat":"Ahenda vestlust","Close chat":"Sulge vestlus","Start a new chat?":"Kas alustada uut vestlust?","Sign in to save your chat history, or continue without signing in.":"Vestlusajaloo salvestamiseks logi sisse või jätka sisse logimata.","Sign in":"Logi sisse","Continue without signing in":"Jätka sisse logimata","Loading chat":"Vestluse laadimine","Chat isn’t available right now":"Vestlus pole praegu saadaval","Something went wrong while loading the chat.":"Vestluse laadimisel tekkis viga.","Try again":"Proovi uuesti","Add to wishlist":"Lisa soovinimekirja","Remove from wishlist":"Eemalda soovinimekirjast","Sort by":"Sorteeri",">> REYTEL replied:":">> REYTEL vastas:","United States":"Ameerika Ühendriigid"}};
 const copy=dictionaries[lang]; if(!copy)return;
 const reviewCount=n=>{const category=new Intl.PluralRules(lang).select(Number(n));const words={lt:{one:'atsiliepimas',few:'atsiliepimai',other:'atsiliepimų'},lv:{one:'atsauksme',zero:'atsauksmju',other:'atsauksmes'},et:{one:'arvustus',other:'arvustust'}}[lang];return n+' '+(words[category]||words.other);};
 const reviewCopy=value=>{let page=value.match(/^Page (\d+)$/i);if(page)return ({lt:'Puslapis ',lv:'Lapa ',et:'Leht '}[lang])+page[1];let m=value.match(/^(?:Based on )?(\d+) reviews?$/);if(m)return reviewCount(m[1]);m=value.match(/^([0-9.]+) (?:stars?|star review|out of 5)$/);if(m)return ({lt:'Įvertinimas: ',lv:'Vērtējums: ',et:'Hinnang: '}[lang])+m[1].replace('.',',')+({lt:' iš 5',lv:' no 5',et:' / 5'}[lang]);m=value.match(/^(\d+)% \((\d+)\) reviews with (\d) star rating$/);if(m)return m[1]+(lang==='et'?'% (':' % (')+m[2]+') — '+({lt:'įvertinimas: ',lv:'vērtējums: ',et:'hinnang: '}[lang])+m[3]+({lt:' iš 5',lv:' no 5',et:' / 5'}[lang]);return null;};
 const localizeElement=el=>{
  if(!(el instanceof Element))return;
  if(el.matches('.wishlist-btn[data-added]')){const label=copy[el.getAttribute('data-added')==='true'?'Remove from wishlist':'Add to wishlist'];if(el.getAttribute('aria-label')!==label)el.setAttribute('aria-label',label);}
  for(const attr of ['aria-label','alt','title']){const value=el.getAttribute(attr);if(copy[value])el.setAttribute(attr,copy[value]);if(value&&el.closest('.jdgm-widget')){const target=reviewCopy(value);if(target)el.setAttribute(attr,target);}const m=value?.match(/^Average rating is ([0-9.]+) stars$/);if(m)el.setAttribute(attr,({lt:'Vidutinis įvertinimas: ',lv:'Vidējais vērtējums: ',et:'Keskmine hinnang: '}[lang])+m[1].replace('.',',')+({lt:' iš 5 žvaigždučių',lv:' no 5 zvaigznēm',et:' tärni 5-st'}[lang]));}
 };
 const localizeText=node=>{
  if(!(node instanceof Text)||node.parentElement?.closest('script,style,textarea,.jdgm-rev__body,.jdgm-rev__author'))return;
  const raw=node.nodeValue||'';const value=raw.replace(/\s+/g,' ').trim();
  if(node.parentElement?.closest('.jdgm-rev__reply')&&!node.parentElement.closest('.jdgm-rev__replier-wrapper'))return;
  const normalized=value.replace(/^(UNLOCK)\s+(5% OFF)$/,'$1 $2');const target=(node.parentElement?.closest('.jdgm-rev__replier-wrapper')&&normalized==='replied:'?{lt:'atsakė:',lv:'atbildēja:',et:'vastas:'}[lang]:null)||copy[normalized]||(node.parentElement?.closest('.jdgm-widget')?reviewCopy(normalized):null);
  if(target&&target!==value)node.nodeValue=raw.replace(value,target);
 };

 const observedChatRoots=new WeakSet();
 const chatUi='button,a,.activator__label,.panel__title,.panel__restore-label,.new-chat-popover__title,.new-chat-popover__description,.panel__unavailable-title,.panel__unavailable-description';
 const scanChat=root=>{
  if(root instanceof Text){if(root.parentElement?.closest(chatUi))localizeText(root);return;}
  if(!(root instanceof Element)&&!(root instanceof ShadowRoot))return;
  const elements=root instanceof Element?[root,...root.querySelectorAll('*')]:Array.from(root.querySelectorAll('*'));
  for(const el of elements){localizeElement(el);if(el.matches('shopify-chat-icon-button')){const label=el.getAttribute('label');if(copy[label])el.setAttribute('label',copy[label]);}if(el.shadowRoot){scanChat(el.shadowRoot);if(!observedChatRoots.has(el.shadowRoot)){observedChatRoots.add(el.shadowRoot);new MutationObserver(changes=>changes.forEach(change=>{if(change.type==='attributes')scanChat(change.target);else if(change.type==='characterData')scanChat(change.target);else change.addedNodes.forEach(scanChat);})).observe(el.shadowRoot,{childList:true,subtree:true,characterData:true,attributes:true,attributeFilter:['aria-label','alt','title','label']});}}}
  const walker=document.createTreeWalker(root,NodeFilter.SHOW_TEXT);let node;while((node=walker.nextNode()))if(node.parentElement?.closest(chatUi))localizeText(node);
 };
 const inspectChat=root=>{if(root instanceof Element&&root.matches('shopify-chat'))scanChat(root);root.querySelectorAll?.('shopify-chat').forEach(scanChat);};
 const scan=root=>{if(root instanceof Text)return localizeText(root);if(!(root instanceof Element))return;inspectChat(root);localizeElement(root);root.querySelectorAll('[aria-label],[alt],[title]').forEach(localizeElement);const walker=document.createTreeWalker(root,NodeFilter.SHOW_TEXT);let node;while((node=walker.nextNode()))localizeText(node);};
 const start=()=>{const badgeStyle=document.createElement('style');badgeStyle.textContent='.jdgm-rev__buyer-badge:before{content:'+JSON.stringify(copy.Verified)+' !important}';document.head.appendChild(badgeStyle);scan(document.body);for(const name of ['shopify-chat','shopify-agent-runtime'])customElements.whenDefined(name).then(()=>inspectChat(document.body));new MutationObserver(changes=>changes.forEach(change=>{if(change.type==='attributes')localizeElement(change.target);else if(change.type==='characterData')localizeText(change.target);else change.addedNodes.forEach(scan);})).observe(document.body,{childList:true,subtree:true,characterData:true,attributes:true,attributeFilter:['aria-label','alt','title','data-added']});};
 if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',start,{once:true});else start();
})();
/* REYTEL_BALTIC_WIDGET_COPY_END */
